/**
 * Rosie — NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../utils/api';
import approveTrip from '../utils/approveTrip';
import { format } from 'date-fns';
import { toET, todayET } from '../utils/et';
import CreateTripModal from '../components/CreateTripModal';
import AssignmentPanel, { getSuggestions } from '../components/AssignmentPanel';
import EditTripModal from '../components/EditTripModal';
import L from 'leaflet';
import openFile from '../utils/openFile';
import 'leaflet/dist/leaflet.css';

function playDispatchAlert() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 660;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
    setTimeout(() => {
      const o2 = ctx.createOscillator();
      const g2 = ctx.createGain();
      o2.connect(g2); g2.connect(ctx.destination);
      o2.frequency.value = 880;
      o2.type = 'sine';
      g2.gain.setValueAtTime(0.25, ctx.currentTime);
      g2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      o2.start(ctx.currentTime);
      o2.stop(ctx.currentTime + 0.3);
    }, 150);
    setTimeout(() => {
      const o3 = ctx.createOscillator();
      const g3 = ctx.createGain();
      o3.connect(g3); g3.connect(ctx.destination);
      o3.frequency.value = 1100;
      o3.type = 'sine';
      g3.gain.setValueAtTime(0.25, ctx.currentTime);
      g3.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
      o3.start(ctx.currentTime);
      o3.stop(ctx.currentTime + 0.4);
    }, 300);
  } catch (e) {}
}

const STATUS_COLORS = {
  pending_approval: { bg: '#FAEEDA', color: '#854F0B', label: 'Pending' },
  approved:         { bg: '#E6F1FB', color: '#185FA5', label: 'Approved' },
  dispatched:       { bg: '#E1F5EE', color: '#0F6E56', label: 'Dispatched' },
  en_route_pickup:  { bg: '#E1F5EE', color: '#0F6E56', label: 'En route' },
  picked_up:        { bg: '#EEEDFE', color: '#534AB7', label: 'Picked up' },
  dropped_off:      { bg: '#E6F1FB', color: '#185FA5', label: 'Dropped off' },
  completed:        { bg: '#f0f0f0', color: '#666',    label: 'Completed' },
  cancelled:        { bg: '#FCEBEB', color: '#A32D2D', label: 'Cancelled' },
};

// BH code badges — shown in ADDITION to Wheelchair/Ambulatory
const BH_CODES = {
  'bh':   { bg: '#FAECE7', color: '#993C1D', label: 'BH' },
  '201':  { bg: '#FAECE7', color: '#993C1D', label: 'BH 201 Voluntary' },
  '302':  { bg: '#FCEBEB', color: '#A32D2D', label: 'BH 302 Involuntary' },
  '303':  { bg: '#FCEBEB', color: '#A32D2D', label: 'BH 303 Involuntary' },
  '304':  { bg: '#FCEBEB', color: '#A32D2D', label: 'BH 304 Involuntary' },
  'da':   { bg: '#FFF0E6', color: '#854F0B', label: 'D&A' },
};

function getTripBadges(trip) {
  const badges = [];
  // ASAP flag — big red attention badge
  if ((trip.notes || '').includes('ASAP REQUEST')) {
    badges.push({ bg: '#C62828', color: 'white', label: 'ASAP — ATTENTION NOW' });
  }
  // Always show wheelchair or ambulatory
  badges.push(trip.requires_wheelchair
    ? { bg: '#EEEDFE', color: '#3C3489', label: 'Wheelchair' }
    : { bg: '#E6F1FB', color: '#185FA5', label: 'Ambulatory' });
  // Also show BH/D&A code if applicable
  const bhBadge = BH_CODES[trip.bh_code] || BH_CODES[trip.patient_type];
  if (bhBadge) badges.push(bhBadge);
  // Pay-before-accept (2026-10-30): unpaid self-booking, auto-cancels at deadline
  const awaitingPayment = trip.payment_due_at && !trip.payment_waived_at
    && trip.prepay_status !== 'paid' && trip.status === 'pending_approval';
  if (awaitingPayment) {
    const due = new Date(trip.payment_due_at).toLocaleString('en-US', { timeZone: 'America/New_York', weekday: 'short', hour: 'numeric', minute: '2-digit' });
    badges.push({ bg: '#C62828', color: 'white', label: `AWAITING PAYMENT $${Number(trip.prepay_amount).toFixed(2)} -- auto-cancels ${due}` });
  } else if (trip.prepay_amount != null) {
    badges.push(trip.prepay_status === 'paid'
      ? { bg: '#E1F5EE', color: '#0F6E56', label: `PREPAID $${Number(trip.prepay_amount).toFixed(2)}` }
      : { bg: '#C62828', color: 'white', label: `PREPAY DUE $${Number(trip.prepay_amount).toFixed(2)}` });
  }
  // Facility form request approved but its confirmation email never went out
  const CONFIRM_RELEVANT = ['approved', 'dispatched', 'en_route_pickup', 'picked_up', 'dropped_off'];
  if (trip.request_channel === 'form' && !trip.confirmation_sent_at && CONFIRM_RELEVANT.includes(trip.status)) {
    badges.push({ bg: '#C62828', color: 'white', label: '⚠ NOT CONFIRMED' });
  }
  // B-110/B-124 (2026-07-30): surface identity gaps while the crew still has
  // the paperwork in hand -- not a week later in QC.
  const notesLc = (trip.notes || '').toLowerCase();
  const active = !['completed', 'cancelled'].includes(trip.status);
  if (active && (notesLc.includes('patient info needed') || (!trip.patient_name_display && !notesLc.includes('patient:')))) {
    badges.push({ bg: '#E65100', color: 'white', label: 'PATIENT INFO NEEDED' });
  }
  // Patient-profile assist flag (Wright/3135, 2026-08-21): loud and red —
  // sending one person to a flagged patient is a safety problem, not a note.
  if (active && notesLc.includes('2-person assist')) {
    badges.push({ bg: '#C62828', color: 'white', label: '2-PERSON ASSIST' });
  }
  const lghBilled = notesLc.includes('source: lgh') || notesLc.includes('source: fax import')
    || ((trip.origin_address || '').toLowerCase().includes('555 n duke') && !trip.return_leg_of);
  if (active && lghBilled && !trip.patient_mrn) {
    badges.push({ bg: '#E65100', color: 'white', label: 'MRN NEEDED (LGH)' });
  }
  return badges;
}

// Keep for backward compat where ptype is used alone
const PATIENT_TYPE_COLORS = {
  'wheelchair': { bg: '#EEEDFE', color: '#3C3489', label: 'Wheelchair' },
  'ambulatory': { bg: '#E6F1FB', color: '#185FA5', label: 'Ambulatory' },
  'bh':         { bg: '#FAECE7', color: '#993C1D', label: 'BH' },
  '201':        { bg: '#FAECE7', color: '#993C1D', label: 'BH 201' },
  '302':        { bg: '#FCEBEB', color: '#A32D2D', label: 'BH 302' },
  '303':        { bg: '#FCEBEB', color: '#A32D2D', label: 'BH 303' },
  '304':        { bg: '#FCEBEB', color: '#A32D2D', label: 'BH 304' },
  'da':         { bg: '#FFF0E6', color: '#854F0B', label: 'D&A' },
};

const PLATE_DOTS = {
  'MBW0677': '#2C2C2A',
  'LZK3338': '#888780',
  'LWR7731': '#0F6E56',
  'MGJ5613': '#993C1D',
  'MGJ5622': '#444441',
  'BA89464': '#B4B2A9',
  'MPJ4505': '#D3D1C7',
};

const isLGH = (transport) => {
  const raw = (transport.request_raw_text || '').toLowerCase();
  const origin = (transport.origin_address || '').toLowerCase();
  return raw.includes('lgh') || raw.includes('lancaster general') || origin.includes('duke st');
};

const isSingleDriverFacility = (transport) => {
  // ONE DRIVER ONLY badge: only for trips originating FROM Willow Valley or LRH.
  // LGH trips going TO these facilities should NOT show this badge.
  const pt = (transport.patient_type || '').toLowerCase();
  if (pt === '201' || pt === '302' || pt === '303' || pt === '304' || pt === 'bh' || pt === 'da') return false;
  const originFacility = (transport.origin_facility_name || '').toLowerCase();
  const origin = (transport.origin_address || '').toLowerCase();
  // Check origin facility name first, fall back to origin address
  if (originFacility) {
    return originFacility.includes('willow valley') || originFacility.includes('lrh') || originFacility.includes('lancaster rehab');
  }
  return origin.includes('willow valley') || origin.includes('good dr') || origin.includes('lancaster rehab');
};

// B-049 (2026-09-10): live "minutes away" from the crew's last GPS ping to
// the next stop. Polls once a minute only while the trip is moving.
function EtaBadge({ trip }) {
  const [eta, setEta] = useState(null);
  const moving = ['dispatched', 'en_route_pickup', 'picked_up'].includes(trip.status);
  useEffect(() => {
    if (!moving) { setEta(null); return undefined; }
    let alive = true;
    const pull = () => api.get(`/eta/${trip.id}`).then(r => { if (alive) setEta(r.data); }).catch(() => {});
    pull();
    const t = setInterval(pull, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [trip.id, trip.status, moving]);
  if (!moving || !eta || !eta.available) return null;
  const label = `${eta.driver ? eta.driver + ' ' : ''}${eta.minutes} min to ${eta.target === 'pickup' ? 'pickup' : 'drop-off'}${eta.stale ? ' (old ping)' : ''}`;
  return (
    <span title={`${eta.miles} mi · location ${eta.ageMinutes} min old`} style={{
      fontSize: 10, padding: '2px 7px', borderRadius: 20, fontWeight: 600, whiteSpace: 'nowrap',
      background: eta.stale ? '#f0f0f0' : '#EEEDFE', color: eta.stale ? '#888' : '#534AB7',
    }}>
      {label}
    </span>
  );
}

function Badge({ bg, color, label }) {
  return (
    <span style={{ background: bg, color, fontSize: 10, padding: '2px 7px', borderRadius: 20, fontWeight: 500, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

// The other leg of a round trip, if it is still live. Used so a cancel can
// take the whole trip at once (Paula 2026-09-25, after cancelling leg by leg
// from the Schedule page took out one leg of the wrong trip).
function findActiveSibling(trip, transports) {
  if (!trip || !Array.isArray(transports)) return null;
  const live = t => t && !['cancelled', 'completed', 'no_show'].includes(t.status);
  const s = transports.find(t => t.id !== trip.id && ((trip.return_leg_of && t.id === trip.return_leg_of) || t.return_leg_of === trip.id));
  return live(s) ? s : null;
}

function RejectModal({ trip, sibling, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const [reschedule, setReschedule] = useState(false);
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [saving, setSaving] = useState(false);
  const [includeSibling, setIncludeSibling] = useState(!!sibling);

  // What is stopping the red button, in words. The button used to sit dead with
  // only a title tooltip to explain itself -- and a tooltip does not exist on a
  // phone, which is where dispatch actually works. Paula hit this on
  // TRP-2026-3744 (2026-09-20): she picked the new time, the button stayed dead,
  // and nothing on screen said the Reason box was the thing still empty.
  const blockedBecause = !reason.trim()
    ? 'Add a reason above to enable this button.'
    : (reschedule && !rescheduleTime ? 'Pick the new pickup time to enable this button.' : null);

  // Only bill cancellation if trip was actually dispatched/in-progress — not pending or just approved
  const isDispatched = ['dispatched', 'en_route_pickup', 'picked_up'].includes(trip.status);
  const hoursUntil = trip.scheduled_time ? (new Date(trip.scheduled_time) - Date.now()) / 3600000 : 999;
  const willBeBilled = hoursUntil < 24 && isDispatched;

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await onConfirm(reason, reschedule ? rescheduleTime : null, false, !reschedule && !!sibling && includeSibling);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
      <div style={{ background: 'white', borderRadius: 12, padding: '1.5rem', width: '100%', maxWidth: 400, margin: '0 16px' }}>
        <h2 style={{ fontSize: 16, fontWeight: 500, marginBottom: 8 }}>Cancel trip</h2>
        <p style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>{trip.transport_number} — {trip.destination_address}</p>

        {willBeBilled && (() => {
          const numTransporters = (trip.assignees || []).length || 1;
          const cancHours = Math.ceil(Number(trip.round_trip_miles || 0) / 50) || 1;
          const cancTotal = cancHours * 68 * numTransporters;
          return (
            <div style={{ background: '#FAEEDA', border: '0.5px solid #FAC775', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#854F0B', marginBottom: 12 }}>
              This trip is within 24 hours — cancellation will be billed.
              {` (${cancHours} hr × $68 × ${numTransporters} transporter${numTransporters > 1 ? 's' : ''} = $${cancTotal.toFixed(2)})`}
            </div>
          );
        })()}

        <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Reason <span style={{ color: '#A32D2D' }}>(required)</span></label>
        <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} placeholder="e.g. Patient discharged, time change, facility cancelled..." style={{ width: '100%', resize: 'none', marginBottom: 6 }} />
        {/* One-tap reasons. A cancellation reason is mandatory (Paula's ruling),
            so on a phone mid-shift the fastest correct path is a chip, not typing. */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {['Time change', 'Patient discharged', 'Facility cancelled', 'Patient refused'].map(r => (
            <button key={r} type="button" onClick={() => setReason(r)}
              style={{ fontSize: 11, padding: '4px 10px', borderRadius: 999, cursor: 'pointer',
                       border: '0.5px solid ' + (reason === r ? '#185FA5' : '#ddd'),
                       background: reason === r ? '#E6F1FB' : '#fff',
                       color: reason === r ? '#185FA5' : '#666' }}>
              {r}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div onClick={() => setReschedule(!reschedule)}
            style={{ width: 32, height: 18, borderRadius: 9, cursor: 'pointer', background: reschedule ? '#185FA5' : '#ddd', position: 'relative', flexShrink: 0 }}>
            <div style={{ width: 14, height: 14, borderRadius: '50%', background: 'white', position: 'absolute', top: 2, left: reschedule ? 16 : 2, transition: 'left 0.15s' }} />
          </div>
          <span style={{ fontSize: 12 }}>Reschedule to a new time</span>
        </div>

        {sibling && !reschedule && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginBottom: 12, background: '#FFF7E6', border: '0.5px solid #FAC775', borderRadius: 8, padding: '8px 10px', cursor: 'pointer' }}>
            <input type="checkbox" checked={includeSibling} onChange={e => setIncludeSibling(e.target.checked)} style={{ width: 18, height: 18 }} />
            <span>
              <strong>Also cancel the other leg</strong> — {sibling.transport_number}
              {sibling.is_will_call && !sibling.return_leg_of ? '' : ''}
              {sibling.scheduled_time ? ' at ' + format(toET(sibling.scheduled_time), 'h:mm a') : ''}
              {sibling.is_will_call ? ' (will call)' : ''}
            </span>
          </label>
        )}

        {reschedule && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>New pickup time</label>
            <input type="datetime-local" value={rescheduleTime} onChange={e => setRescheduleTime(e.target.value)} style={{ width: '100%' }} />
          </div>
        )}

        {blockedBecause && (
          <div style={{ fontSize: 12, color: '#A32D2D', background: '#FCEBEB', border: '0.5px solid #F09595',
                        borderRadius: 8, padding: '6px 10px', marginBottom: 10 }}>
            {blockedBecause}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ fontSize: 13 }}>Keep trip</button>
          <button onClick={handleConfirm} disabled={saving || !!blockedBecause}
            title={blockedBecause || undefined}
            style={{ fontSize: 13, background: '#FCEBEB', color: '#A32D2D', border: '0.5px solid #F09595', padding: '6px 14px' }}>
            {saving ? 'Processing...' : reschedule ? 'Cancel & reschedule' : (sibling && includeSibling ? 'Cancel both legs' : 'Cancel trip')}
          </button>
        </div>
      </div>
    </div>
  );
}

function LiveMapPanel({ locations }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    if (!mapRef.current) return;
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapRef.current, { zoomControl: true }).setView([40.0379, -76.3055], 11);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '',
        maxZoom: 18,
      }).addTo(mapInstanceRef.current);
    }
    // Clear old markers
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    // Add new markers
    locations.forEach(loc => {
      const initial = loc.driver_name.split(' ')[0][0];
      const icon = L.divIcon({
        className: '',
        html: '<div style="background:#185FA5;color:white;width:24px;height:24px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:600;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.3)">' + initial + '</div>',
        iconSize: [24, 24],
        iconAnchor: [12, 12],
      });
      const marker = L.marker([Number(loc.latitude), Number(loc.longitude)], { icon, title: loc.driver_name })
        .addTo(mapInstanceRef.current);
      marker.bindPopup('<div style="font-size:12px"><strong>' + loc.driver_name + '</strong><br>' + (loc.vehicle_name || '') + '<br>' + (loc.destination_address || '') + '</div>');
      markersRef.current.push(marker);
    });

    // Fit bounds if we have markers
    if (locations.length > 0) {
      const points = locations.map(l => [Number(l.latitude), Number(l.longitude)]);
      points.push([40.0379, -76.3055]); // home base
      mapInstanceRef.current.fitBounds(points, { padding: [50, 50] });
    }
  }, [locations]);

  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
    };
  }, []);

  if (locations.length === 0) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8, color: '#185FA5' }}>
        Live driver locations ({locations.length})
      </div>
      <div ref={mapRef} style={{
        width: '100%', height: 300, borderRadius: 12,
        border: '0.5px solid #e0e0db', overflow: 'hidden',
      }} />
    </div>
  );
}


// Free-window math shared by the vehicle and driver strips (Paula 2026-10-08:
// "it shows they aren't available until a time way in the future, but they're
// actually open for a while"). The old code took the LATEST return time of
// every remaining trip today, so a 6 PM run made someone look busy all day.
// Now: busy only if a trip covers NOW (or is in progress); when busy, free at
// the end of the back-to-back chain; when free, free UNTIL the next start.
function tripEnd(t) {
  return t.estimated_return_time
    ? new Date(t.estimated_return_time)
    : new Date(new Date(t.scheduled_time).getTime() + (Number(t.estimated_duration_minutes) || 90) * 60000);
}
function computeFreeWindow(trips, now) {
  const sorted = [...trips].filter(t => t.scheduled_time).sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time));
  const inProgress = (t) => ['en_route_pickup', 'picked_up', 'dropped_off'].includes(t.status);
  const lead = 10 * 60000; // count a trip as "now" 10 min before its pickup
  let current = sorted.find(t => inProgress(t) || (new Date(t.scheduled_time).getTime() - lead <= now.getTime() && tripEnd(t) > now));
  if (current) {
    let freeAt = tripEnd(current);
    for (const t of sorted) {
      const st = new Date(t.scheduled_time);
      if (st > now && st.getTime() - lead <= freeAt.getTime() && tripEnd(t) > freeAt) freeAt = tripEnd(t);
    }
    return { busyNow: true, currentTrip: current, freeAt, freeUntil: null, nextTrip: null };
  }
  const nextTrip = sorted.find(t => new Date(t.scheduled_time) > now) || null;
  return { busyNow: false, currentTrip: null, freeAt: null, freeUntil: nextTrip ? new Date(nextTrip.scheduled_time) : null, nextTrip };
}

function WillCallCard({ trip, onEdit, onCancel, onApprove }) {
  const ptype = PATIENT_TYPE_COLORS[trip.patient_type];
  const statusInfo = STATUS_COLORS[trip.status] || STATUS_COLORS.pending_approval;

  const isPatientReady = !!trip.patient_ready_at;

  return (
    <div style={{
      border: isPatientReady ? '2px solid #1D9E75' : '1px solid #BA7517',
      borderLeft: isPatientReady ? '4px solid #1D9E75' : '4px solid #BA7517',
      borderRadius: 10, padding: '12px 14px', marginBottom: 8,
      background: isPatientReady ? '#E8F8F055' : '#FAEEDA55',
      animation: isPatientReady ? 'patientReadyPulse 2s ease-in-out 3' : 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
            {isPatientReady ? (
              <span style={{ fontSize: 11, fontWeight: 600, background: '#1D9E75', color: 'white', padding: '2px 8px', borderRadius: 20 }}>
                ✅ PATIENT READY
              </span>
            ) : (
              <span style={{ fontSize: 11, fontWeight: 600, background: '#BA7517', color: 'white', padding: '2px 8px', borderRadius: 20 }}>
                📞 WILL CALL
              </span>
            )}
            <Badge {...statusInfo} />
            {trip.return_leg_of && <Badge bg="#FFF7E6" color="#854F0B" label="↩ Return leg" />}
          </div>
          {/* Patient name is the headline (Paula 2026-09-16). Erika was picking
              the wrong will-call because the card led with the destination, and
              11 of 12 open will-calls return to 675 Willow Valley Sq -- so every
              card read the same. The name was in the data all along, just never
              rendered. */}
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 3 }}>
            {trip.patient_name_display || <span style={{ color: '#A32D2D' }}>Name not on file</span>}
          </div>
          {/* Who dropped them off -- dispatch tries to send the same driver back. */}
          {trip.outbound_drivers && (
            <div style={{ fontSize: 12, fontWeight: 600, color: '#0F6E56', background: '#E1F5EE',
              display: 'inline-block', padding: '2px 8px', borderRadius: 20, marginBottom: 4 }}>
              Dropped off by {trip.outbound_drivers} — send them back if you can
            </div>
          )}
          <div style={{ fontSize: 12, color: '#444', marginBottom: 2 }}>
            Waiting at <strong>{(trip.origin_address || 'TBD').split(',')[0]}</strong>
          </div>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>
            Take to: {trip.destination_address || 'Destination TBD'}
          </div>
          <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
            {getTripBadges(trip).map((b, i) => <Badge key={i} {...b} />)}
            <Badge bg="#f0f0f0" color="#666" label={trip.transport_number} />
          </div>
          {trip.outbound_scheduled_time && (
            <div style={{ fontSize: 11, color: '#666', marginTop: 6 }}>
              Dropped off at <strong>{trip.outbound_destination ? trip.outbound_destination.split(',')[0] : '?'}</strong> at {format(toET(trip.outbound_scheduled_time), 'h:mm a')} ({trip.outbound_transport_number?.replace('TRP-2026-0','#')})
            </div>
          )}
          {isPatientReady ? (
            <div style={{ fontSize: 11, color: '#1D9E75', marginTop: 4, fontWeight: 500 }}>
              Patient tapped ready at {format(toET(trip.patient_ready_at), 'h:mm a')} — assign a driver and dispatch!
            </div>
          ) : (
            <div style={{ fontSize: 11, color: '#854F0B', marginTop: 4, fontStyle: 'italic' }}>
              When the patient calls, tap Set pickup time to enter it and assign.
            </div>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
        {trip.status === 'pending_approval' && onApprove && (
          <button onClick={() => onApprove(trip)} style={{ fontSize: 12, padding: '5px 12px', background: '#1D9E75', color: 'white', border: '0.5px solid #1D9E75', borderRadius: 6, cursor: 'pointer', fontWeight: 500 }}>
            Approve
          </button>
        )}
        <button onClick={() => onCancel(trip)} style={{ fontSize: 12, padding: '5px 12px', background: '#FCEBEB', color: '#A32D2D', border: '0.5px solid #F09595', borderRadius: 6, cursor: 'pointer' }}>
          Cancel
        </button>
        <button onClick={() => onEdit(trip)} style={{ fontSize: 12, padding: '5px 12px', background: '#185FA5', color: 'white', border: '0.5px solid #185FA5', borderRadius: 6, cursor: 'pointer' }}>
          Set pickup time
        </button>
      </div>
    </div>
  );
}

function PendingTripCard({ trip, vehicles, drivers, existingTransports, isRoundTrip, onApprove, onReject, onAssign, onEdit, onMerge, onApproveDispatch }) {
  const ptype = PATIENT_TYPE_COLORS[trip.patient_type];
  const lgh = isLGH(trip);
  // Only LGH-pipeline trips skip the approve step. Portal ('form') trips always
  // need approval, even when a leg picks up at LGH (e.g. a return leg home).
  const skipApproval = lgh && trip.request_channel !== 'form';
  const tripDate = trip.scheduled_time ? toET(trip.scheduled_time) : null;
  const tripTime = tripDate ? format(tripDate, 'h:mm a') : (trip.is_will_call ? 'Will call' : '--:--');
  // Return estimate exists from creation (AWS route time) -- show it before
  // dispatch too, so a dispatcher can see when the crew is back without
  // having to assign the trip first. Same fallback math as TripCard.
  const pendingReturn = trip.estimated_return_time
    ? new Date(trip.estimated_return_time)
    : (trip.scheduled_time
      ? new Date(new Date(trip.scheduled_time).getTime() + (parseFloat(trip.estimated_duration_minutes) || (parseFloat(trip.round_trip_miles) || 40) * 1.5) * 60000)
      : null);
  const pendingReturnET = pendingReturn ? toET(pendingReturn) : null;
  const returnNextDay = pendingReturnET && tripDate && format(pendingReturnET, 'yyyy-MM-dd') !== format(tripDate, 'yyyy-MM-dd');
  const returnLabel = pendingReturnET
    ? `back ~${format(pendingReturnET, 'h:mm a')}${returnNextDay ? ' (next day)' : ''}`
    : null;
  const isToday = tripDate ? format(tripDate, 'yyyy-MM-dd') === todayET() : true;
  const isFuture = trip.scheduled_time ? new Date(trip.scheduled_time) > new Date() && !isToday : false;
  const dateLabel = tripDate ? (isToday ? 'Today' : format(tripDate, 'EEE MMM d')) : '';
  // Always run suggestions to get driver/vehicle recommendations.
  // But for future trips (>30 min out), suppress the "cannot fulfill" warning since drivers may not be on shift yet.
  const tripIsNow = tripDate && (tripDate.getTime() - Date.now()) < 30 * 60 * 1000;
  const suggestions = vehicles.length > 0
    ? getSuggestions(trip, vehicles, drivers, existingTransports)
    : { suggestedVehicle: null, suggestedDrivers: [], canFulfill: true, reason: null };
  const canFulfill = tripIsNow ? suggestions.canFulfill : true;
  const reason = tripIsNow ? suggestions.reason : null;
  const { suggestedVehicle, suggestedDrivers } = suggestions;

  return (
    <div style={{ border: `0.5px solid ${isFuture ? '#B5D4F4' : '#FAC775'}`, borderRadius: 10, padding: '12px 14px', marginBottom: 8, background: isFuture ? '#E6F1FB22' : '#FAEEDA22' }}>
      <div style={{ flex: 1, marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 500, background: isFuture ? '#185FA5' : '#BA7517', color: 'white', padding: '2px 8px', borderRadius: 20 }}>
            {dateLabel}
          </span>
        </div>
        <div style={{ fontSize: 13, fontWeight: 500 }}>{tripTime} — {trip.destination_address || 'Destination TBD'}</div>
        <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>From: {trip.origin_address || 'TBD'}</div>
        {returnLabel && (
          <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
            {'↩'} {returnLabel}{trip.estimated_duration_minutes ? ` · ${Math.round(parseFloat(trip.estimated_duration_minutes) / 60 * 10) / 10} h round trip` : ''}
          </div>
        )}
        <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
          {getTripBadges(trip).map((b, i) => <Badge key={i} {...b} />)}
          {lgh && <Badge bg="#E1F5EE" color="#0F6E56" label="LGH" />}
          {isRoundTrip && <Badge bg="#FFF7E6" color="#854F0B" label={trip.return_leg_of ? '↩ Return leg' : '⇄ Round trip'} />}
          {trip.is_will_call && <Badge bg="#FAEEDA" color="#854F0B" label="Will call" />}
          {trip.fax_file_id && (
            <a href="#" onClick={(e) => openFile(trip.fax_file_id, e)}
              style={{ fontSize: 10, color: '#185FA5', textDecoration: 'none', padding: '2px 7px', background: '#E6F1FB', borderRadius: 20 }}>
              View fax
            </a>
          )}
          {trip.bill_to_type === 'patient' && (
            <a href={`/api/patient-estimate/${trip.id}`} target="_blank" rel="noreferrer"
              style={{ fontSize: 10, color: '#854F0B', textDecoration: 'none', padding: '2px 7px', background: '#FFF3E0', borderRadius: 20, fontWeight: 600 }}>
              Private Pay — View Estimate
            </a>
          )}
          <Badge bg="#f0f0f0" color="#666" label={trip.transport_number} />
        </div>
      </div>

      {/* Rosie's proposed dispatch (from chatbot proposeDispatch) */}
      {trip.proposed_vehicle_id && trip.proposed_drivers && (
        <div style={{ background: '#E1F5EE', borderRadius: 8, padding: '8px 10px', marginBottom: 10, border: '0.5px solid #5DCAA5' }}>
          <div style={{ fontSize: 11, color: '#0F6E56', fontWeight: 500, marginBottom: 4 }}>Rosie suggests</div>
          <div style={{ fontSize: 12, color: '#085041', fontWeight: 500 }}>
            {(typeof trip.proposed_drivers === 'string' ? JSON.parse(trip.proposed_drivers) : trip.proposed_drivers).map(d => d.name.split(' ')[0]).join(' + ')}
            {trip.proposed_vehicle_name ? ` + ${trip.proposed_vehicle_name}` : ''}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <button onClick={() => onApproveDispatch(trip)} style={{ fontSize: 11, padding: '4px 10px', background: '#0F6E56', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 500 }}>Approve</button>
            <button onClick={() => onAssign(trip)} style={{ fontSize: 11, padding: '4px 10px', background: 'white', color: '#0F6E56', border: '0.5px solid #5DCAA5', borderRadius: 4, cursor: 'pointer' }}>Change</button>
          </div>
        </div>
      )}

      {!trip.proposed_vehicle_id && !canFulfill ? (
        <div style={{ background: '#FCEBEB', borderRadius: 8, padding: '8px 10px', marginBottom: 10, border: '0.5px solid #F09595' }}>
          <div style={{ fontSize: 11, color: '#A32D2D', fontWeight: 500, marginBottom: 2 }}>Cannot auto-fulfill</div>
          <div style={{ fontSize: 12, color: '#A32D2D' }}>{reason}</div>
        </div>
      ) : !trip.proposed_vehicle_id && (suggestedVehicle || suggestedDrivers.length > 0) && (
        <div style={{ background: 'white', borderRadius: 8, padding: '8px 10px', marginBottom: 10, border: '0.5px solid #e0e0db' }}>
          <div style={{ fontSize: 11, color: '#aaa', marginBottom: 4 }}>Suggested assignment</div>
          {suggestedVehicle && <div style={{ fontSize: 12, color: '#185FA5', fontWeight: 500 }}>{suggestedVehicle.name}</div>}
          {suggestedDrivers.length > 0 && <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{suggestedDrivers.map(d => d.name).join(' + ')}</div>}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={() => onReject(trip)} style={{ fontSize: 12, padding: '5px 12px', background: '#FCEBEB', color: '#A32D2D', border: '0.5px solid #F09595' }}>Reject</button>
        <button onClick={() => onEdit(trip)} style={{ fontSize: 12, padding: '5px 12px' }}>Edit</button>
        {needsPatientIdentity(trip) && <PaperworkPhotoButton trip={trip} big />}
        <button onClick={() => onMerge(trip)} style={{ fontSize: 12, padding: '5px 12px', background: '#EEEDFE', color: '#534AB7', border: '0.5px solid #B8B4E8' }}>Merge</button>
        {trip.status === 'approved' ? (
          <button onClick={() => onAssign(trip)} style={{ fontSize: 12, padding: '5px 12px', background: '#E1F5EE', color: '#0F6E56', border: '0.5px solid #5DCAA5' }}>Assign & dispatch</button>
        ) : skipApproval ? (
          <>
            {/* Staff-entered LGH trips usually go straight to dispatch, but a
                future leg (will-call return, next-week booking) has nobody to
                assign yet. Without a plain Approve it sits in Needs attention
                with no way out (3986, Paula 2026-10-01). */}
            <button onClick={() => onApprove(trip)} style={{ fontSize: 12, padding: '5px 12px' }}>Approve only</button>
            <button onClick={() => onAssign(trip)} style={{ fontSize: 12, padding: '5px 12px', background: '#E1F5EE', color: '#0F6E56', border: '0.5px solid #5DCAA5' }}>Assign & dispatch</button>
          </>
        ) : (
          <>
            <button onClick={() => onApprove(trip)} style={{ fontSize: 12, padding: '5px 12px', background: '#185FA5', color: 'white', border: '0.5px solid #185FA5' }}>Approve trip</button>
            <button onClick={() => onAssign(trip)} style={{ fontSize: 12, padding: '5px 12px', background: '#E1F5EE', color: '#0F6E56', border: '0.5px solid #5DCAA5' }}>Approve & assign</button>
          </>
        )}
      </div>
    </div>
  );
}

// Paula 2026-10-27: an LGH trip needs the patient name AND the MRN. Without a
// fax, the dispatcher snaps a photo of the paperwork right here (phone camera)
// and Rosie reads the name + MRN off it (backend paperworkReader).
function needsPatientIdentity(trip) {
  const notesLc = (trip.notes || '').toLowerCase();
  const lgh = notesLc.includes('source: lgh') || notesLc.includes('source: fax import')
    || ((trip.origin_address || '').toLowerCase().includes('555 n duke') && !trip.return_leg_of);
  return notesLc.includes('patient info needed') || !String(trip.patient_name_display || '').trim() || (lgh && !trip.patient_mrn);
}

function PaperworkPhotoButton({ trip, onDone, big }) {
  const [busy, setBusy] = useState(false);
  return (
    <label style={{
      fontSize: big ? 12 : 11, padding: big ? '5px 12px' : '3px 8px', borderRadius: 6, cursor: 'pointer',
      background: '#E65100', color: 'white', border: '0.5px solid #E65100', opacity: busy ? 0.6 : 1, display: 'inline-block',
    }}>
      {busy ? 'Reading paperwork...' : 'Add paperwork photo'}
      <input type="file" accept="image/*,.pdf" capture="environment" disabled={busy} style={{ display: 'none' }} onChange={async (e) => {
        let f = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!f) return;
        setBusy(true);
        try {
          // Phone photos run 3-8 MB; shrink to ~2000px JPEG before upload so it
          // uploads fast on cell data and stays under the reader's 5 MB limit.
          if (/^image\//.test(f.type) && f.size > 1.5 * 1024 * 1024) {
            try {
              const bmp = await createImageBitmap(f);
              const scale = Math.min(1, 2000 / Math.max(bmp.width, bmp.height));
              const c = document.createElement('canvas');
              c.width = Math.round(bmp.width * scale); c.height = Math.round(bmp.height * scale);
              c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
              const blob = await new Promise(res => c.toBlob(res, 'image/jpeg', 0.8));
              if (blob) f = new File([blob], 'paperwork.jpg', { type: 'image/jpeg' });
            } catch (shrinkErr) { /* upload the original; the server shrinks it */ }
          }
          const fd = new FormData();
          fd.append('file', f);
          fd.append('category', 'transport');
          fd.append('docType', 'fax');
          fd.append('transportId', trip.id);
          const r = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          const pw = r.data.paperwork || {};
          const got = [pw.filled && pw.filled.name ? 'name' : null, pw.filled && pw.filled.mrn ? 'MRN ' + pw.filled.mrn : null].filter(Boolean);
          alert('Paperwork attached to ' + trip.transport_number + '.'
            + (got.length ? '\nRead from it: ' + got.join(', ') + '.' : '')
            + (pw.mismatch ? '\n\nCHECK: ' + pw.mismatch : '')
            + (pw.err ? '\n\nCould not read it (' + pw.err + ') -- type the name and MRN with Edit.' : '')
            + (!pw.err && !pw.mismatch && pw.complete === false ? '\n\nStill missing the name or MRN -- type it with Edit.' : ''));
          if (onDone) onDone();
        } catch (err) {
          alert(err.response?.data?.error || 'Upload failed');
        } finally { setBusy(false); }
      }} />
    </label>
  );
}

function TripCard({ trip, isRoundTrip, onAssign, onEdit, onComplete, onCancel, onRefresh }) {
  const status = STATUS_COLORS[trip.status] || STATUS_COLORS.approved;
  const ptype = PATIENT_TYPE_COLORS[trip.patient_type];
  const estReturn = trip.estimated_return_time
    ? new Date(trip.estimated_return_time)
    : (trip.scheduled_time
      ? new Date(new Date(trip.scheduled_time).getTime() + (parseFloat(trip.estimated_duration_minutes) || (parseFloat(trip.round_trip_miles) || 40) * 1.5) * 60000)
      : null);
  const assignees = trip.assignees || [];
  const timeLabel = trip.scheduled_time
    ? format(toET(trip.scheduled_time), 'h:mm a')
    : (trip.is_will_call ? 'Will call' : '--:--');
  const delayMins = Number(trip.delay_minutes) || 0;
  const [showDelay, setShowDelay] = useState(false);
  const [delayInput, setDelayInput] = useState(String(delayMins));
  const [delayReason, setDelayReason] = useState(trip.delay_reason || '');
  const [linkCopied, setLinkCopied] = useState(false);

  const copyPatientLink = () => {
    const token = trip.willcall_return_token;
    if (!token) return;
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/ready/${token}`;
    navigator.clipboard.writeText(link).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  return (
    <div className="trip-card" style={{ border: '0.5px solid #e0e0db', borderRadius: 8, padding: '10px 12px', marginBottom: 6, background: 'white', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
      <div className="trip-time" style={{ minWidth: 72 }}>
        <div style={{ fontWeight: 500, fontSize: 13 }}>{timeLabel}</div>
        {delayMins > 0 && (
          <div style={{ fontSize: 10, fontWeight: 500, color: '#A32D2D', marginTop: 1 }}>
            +{delayMins} min late
          </div>
        )}
        <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{estReturn ? `↩ ${format(toET(estReturn), 'h:mm a')}` : ''}</div>
        <div style={{ marginTop: 3 }}><EtaBadge trip={trip} /></div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{trip.destination_address || 'Destination TBD'}</div>
        <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{trip.transport_number}</div>
        {assignees.length > 0 && (
          <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            {isSingleDriverFacility(trip) && assignees.length === 1 && (
              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#FCEBEB', color: '#A32D2D', fontWeight: 700, border: '1.5px solid #F09595' }}>
                ONE DRIVER ONLY
              </span>
            )}
            {assignees.map((a, i) => (
              <span key={i} style={{ fontSize: 11, padding: '2px 7px', borderRadius: 20, background: i === 0 ? '#E1F5EE' : '#E6F1FB', color: i === 0 ? '#085041' : '#0C447C', fontWeight: 600 }}>
                {a.name}{a.acknowledged ? ' \uD83D\uDC4D' : a.notified ? ' \u23F3' : ''}
              </span>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
          {getTripBadges(trip).map((b, i) => <Badge key={i} {...b} />)}
          {isRoundTrip && <Badge bg="#FFF7E6" color="#854F0B" label={trip.return_leg_of ? '↩ Return' : '⇄ Round trip'} />}
          {trip.is_will_call && <Badge bg="#FAEEDA" color="#854F0B" label="Will call" />}
          {trip.fax_file_id && (
            <a href="#" onClick={(e) => openFile(trip.fax_file_id, e)}
              style={{ fontSize: 10, color: '#185FA5', textDecoration: 'none', padding: '2px 7px', background: '#E6F1FB', borderRadius: 20 }}>
              View fax
            </a>
          )}
          {trip.bill_to_type === 'patient' && (
            <a href={`/api/patient-estimate/${trip.id}`} target="_blank" rel="noreferrer"
              style={{ fontSize: 10, color: '#854F0B', textDecoration: 'none', padding: '2px 7px', background: '#FFF3E0', borderRadius: 20, fontWeight: 600 }}>
              Private Pay — Estimate
            </a>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
        <Badge {...status} />
        {showDelay && (
          <div style={{ background: '#FAEEDA', border: '0.5px solid #FAC775', borderRadius: 6, padding: '6px 8px', marginBottom: 4 }}>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 4 }}>
              <select value={delayInput} onChange={e => setDelayInput(e.target.value)} style={{ fontSize: 11, width: 80 }}>
                <option value="0">No delay</option>
                <option value="10">+10 min</option>
                <option value="15">+15 min</option>
                <option value="20">+20 min</option>
                <option value="30">+30 min</option>
                <option value="45">+45 min</option>
                <option value="60">+1 hr</option>
                <option value="90">+1.5 hr</option>
              </select>
              <input value={delayReason} onChange={e => setDelayReason(e.target.value)} placeholder="Reason (traffic, wait...)" style={{ flex: 1, fontSize: 10, padding: '3px 6px' }} />
              <button onClick={async () => {
                try {
                  await api.put(`/transports/${trip.id}/delay`, { delayMinutes: Number(delayInput), reason: delayReason });
                  setShowDelay(false);
                  if (onRefresh) onRefresh();
                } catch (e) { alert('Failed'); }
              }} style={{ fontSize: 10, padding: '3px 8px', background: '#185FA5', color: 'white', border: 'none', borderRadius: 4 }}>Save</button>
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
          <button onClick={() => onEdit(trip)} style={{ fontSize: 11, padding: '3px 8px' }}>Edit</button>
          {trip.status !== 'cancelled' && needsPatientIdentity(trip) && <PaperworkPhotoButton trip={trip} onDone={onRefresh} />}
          {['dispatched', 'en_route_pickup', 'picked_up', 'dropped_off'].includes(trip.status) && (
            <button onClick={() => setShowDelay(!showDelay)} style={{ fontSize: 11, padding: '3px 8px', background: delayMins > 0 ? '#FAEEDA' : 'white', color: '#854F0B', border: '0.5px solid #FAC775' }}>
              {delayMins > 0 ? `Late +${delayMins}m` : 'Running late'}
            </button>
          )}
          {['approved', 'dispatched', 'en_route_pickup', 'picked_up', 'dropped_off'].includes(trip.status) && (
            <button onClick={() => onCancel(trip)} style={{ fontSize: 11, padding: '3px 8px', background: '#FCEBEB', color: '#A32D2D', border: '0.5px solid #F09595' }}>
              Cancel
            </button>
          )}
          {['approved', 'dispatched', 'en_route_pickup', 'picked_up', 'dropped_off'].includes(trip.status) && (
            <button onClick={() => onComplete(trip)} style={{ fontSize: 11, padding: '3px 8px', background: '#185FA5', color: 'white', border: '0.5px solid #185FA5' }}>
              Complete
            </button>
          )}
          {['approved', 'dispatched', 'en_route_pickup', 'picked_up', 'dropped_off'].includes(trip.status) && (
            <button onClick={() => onAssign(trip)} style={{ fontSize: 11, padding: '3px 8px', background: '#E1F5EE', color: '#0F6E56', border: '0.5px solid #5DCAA5' }}>
              {trip.status === 'approved' ? 'Assign' : 'Reassign'}
            </button>
          )}
          {trip.willcall_return_token && (
            <button onClick={copyPatientLink} style={{
              fontSize: 11, padding: '3px 8px', borderRadius: 4, cursor: 'pointer',
              background: linkCopied ? '#E8F8F0' : '#f0f0f0',
              color: linkCopied ? '#1D9E75' : '#333',
              border: linkCopied ? '0.5px solid #1D9E75' : '0.5px solid #ddd',
            }}>
              {linkCopied ? 'Copied!' : 'Copy patient link'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PriorityModal({ drivers, onClose }) {
  const [priorities, setPriorities] = useState([]);
  const [saving, setSaving] = useState(false);
  const today = todayET();
  const availableDrivers = drivers.filter(d => d.isAvailable);

  useEffect(() => {
    api.get(`/drivers/dispatch-priorities?date=${today}`)
      .then(r => {
        if (r.data.priorities?.length > 0) {
          setPriorities(r.data.priorities.map(p => ({
            driverId: p.driver_id,
            name: `${p.first_name} ${p.last_name}`,
            pairWithId: p.pair_with_driver_id,
            pairName: p.pair_first_name ? `${p.pair_first_name} ${p.pair_last_name}` : '',
            role: p.role,
            notes: p.notes || '',
          })));
        }
      }).catch(() => {});
  }, []);

  const addDriver = (driverId) => {
    const d = availableDrivers.find(d => d.id === driverId);
    if (!d || priorities.find(p => p.driverId === driverId)) return;
    setPriorities(prev => [...prev, { driverId, name: `${d.firstName} ${d.lastName || d.name?.split(' ')[1] || ''}`, pairWithId: null, pairName: '', role: 'primary', notes: '' }]);
  };

  const removeDriver = (driverId) => setPriorities(prev => prev.filter(p => p.driverId !== driverId));

  const moveUp = (i) => {
    if (i === 0) return;
    const arr = [...priorities];
    [arr[i-1], arr[i]] = [arr[i], arr[i-1]];
    setPriorities(arr);
  };

  const setPair = (i, pairId) => {
    const arr = [...priorities];
    const pair = availableDrivers.find(d => d.id === pairId);
    arr[i].pairWithId = pairId || null;
    arr[i].pairName = pair ? `${pair.firstName} ${pair.lastName || ''}` : '';
    setPriorities(arr);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/drivers/dispatch-priorities', {
        date: today,
        priorities: priorities.map((p, i) => ({ driverId: p.driverId, order: i + 1, pairWithId: p.pairWithId, role: p.role, notes: p.notes })),
      });
      onClose();
    } catch (e) { alert('Save failed'); }
    finally { setSaving(false); }
  };

  const notAdded = availableDrivers.filter(d => !priorities.find(p => p.driverId === d.id));

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
      <div style={{ background: 'white', borderRadius: 12, padding: '1.5rem', width: '100%', maxWidth: 450, margin: '0 16px', maxHeight: '85vh', overflow: 'auto' }}>
        <h2 style={{ fontSize: 16, fontWeight: 500, marginBottom: 4 }}>Today's dispatch priorities</h2>
        <p style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>Set the order Rosie should use when suggesting drivers. #1 gets trips first.</p>

        {priorities.map((p, i) => (
          <div key={p.driverId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: '0.5px solid #e0e0db', borderRadius: 8, marginBottom: 6, background: '#f9f9f8' }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#185FA5', minWidth: 20 }}>{i + 1}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{p.name}</div>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 2 }}>
                <select value={p.pairWithId || ''} onChange={e => setPair(i, e.target.value)} style={{ fontSize: 10, padding: '2px 4px' }}>
                  <option value="">Solo / any partner</option>
                  {availableDrivers.filter(d => d.id !== p.driverId).map(d => (
                    <option key={d.id} value={d.id}>Pair with {d.firstName}</option>
                  ))}
                </select>
              </div>
            </div>
            <button onClick={() => moveUp(i)} disabled={i === 0} style={{ fontSize: 11, padding: '2px 6px' }}>Up</button>
            <button onClick={() => removeDriver(p.driverId)} style={{ fontSize: 11, padding: '2px 6px', color: '#A32D2D' }}>X</button>
          </div>
        ))}

        {notAdded.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <select onChange={e => { addDriver(e.target.value); e.target.value = ''; }} style={{ width: '100%', fontSize: 12, padding: '8px' }}>
              <option value="">+ Add driver to priority list...</option>
              {notAdded.map(d => <option key={d.id} value={d.id}>{d.firstName} {d.lastName || d.name?.split(' ')[1] || ''}</option>)}
            </select>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onClose} style={{ fontSize: 13 }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ fontSize: 13, background: '#185FA5', color: 'white', border: '0.5px solid #185FA5', padding: '6px 16px' }}>
            {saving ? 'Saving...' : 'Save priorities'}
          </button>
        </div>
      </div>
    </div>
  );
}

function NextAvailablePanel({ vehicles, transports, drivers }) {
  const now = new Date();
  const activeVehicles = vehicles.filter(v => v.status === 'active');
  if (activeVehicles.length === 0) return null;

  const todayStr = todayET();
  // For each vehicle, find when it's next free (today's trips only)
  const vehicleAvail = activeVehicles.map(v => {
    const vTrips = transports
      .filter(t => {
        if (t.vehicle_id !== v.id) return false;
        if (['cancelled', 'completed'].includes(t.status)) return false;
        if (!t.scheduled_time) return false;
        return format(toET(t.scheduled_time), 'yyyy-MM-dd') === todayStr;
      })
      .sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time));

    const win = computeFreeWindow(vTrips, now);
    const currentTrip = win.currentTrip || win.nextTrip;
    const assignedDrivers = currentTrip
      ? (currentTrip.assignees || []).map(a => a.name?.split(' ')[0]).join(' & ')
      : '';
    const isOnTrip = !!(win.currentTrip && ['en_route_pickup', 'picked_up', 'dropped_off'].includes(win.currentTrip.status));

    return {
      vehicle: v,
      nextFree: win.freeAt,
      freeUntil: win.freeUntil,
      isFreeNow: !win.busyNow,
      isOnTrip,
      currentDest: win.currentTrip?.destination_address,
      drivers: assignedDrivers,
    };
  });

  // Also find on-call drivers who are already actively working (have dispatched trips today)
  // Don't include pure on-call who aren't doing anything

  // Sort: free now first, then by soonest available
  vehicleAvail.sort((a, b) => {
    if (a.isFreeNow && !b.isFreeNow) return -1;
    if (!a.isFreeNow && b.isFreeNow) return 1;
    return (a.nextFree || 0) - (b.nextFree || 0);
  });

  return (
    <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 10, padding: '10px 14px' }}>
      <div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
        Vehicles — next available
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {vehicleAvail.map(va => (
          <div key={va.vehicle.id} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '5px 10px', borderRadius: 8,
            background: va.isFreeNow ? '#E1F5EE' : va.isOnTrip ? '#FAEEDA' : '#f5f5f3',
            border: `0.5px solid ${va.isFreeNow ? '#5DCAA5' : va.isOnTrip ? '#FAC775' : '#e0e0db'}`,
            fontSize: 11,
          }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              background: va.isFreeNow ? '#0F6E56' : va.isOnTrip ? '#BA7517' : '#999',
            }} />
            <div>
              <div style={{ fontWeight: 500, fontSize: 11 }}>
                {va.vehicle.name}
              </div>
              <div style={{ fontSize: 10, color: va.isFreeNow ? '#0F6E56' : '#666' }}>
                {va.isFreeNow
                  ? `Available now${va.freeUntil ? ` until ${format(toET(va.freeUntil), 'h:mm a')}` : ''}`
                  : va.nextFree
                    ? `Busy · free ~${format(toET(va.nextFree), 'h:mm a')}`
                    : 'Available'
                }
                {va.drivers ? ` · ${va.drivers}` : ''}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const ON_CALL_IDS = new Set([
  '8ade9877-085a-41f7-b4c7-210081d068eb', // Chance
  '9499e53f-c898-423d-82aa-1bd77718584a', // Brankica
  'ffcca39d-e5f7-440b-bccc-37cd6082119e', // Scott
  '85e1dd12-0fab-43de-b64c-90b4055e2a9a', // Brosey
  'db6093d4-031b-4b4a-aa40-ece433481b0e', // Dejan
]);

// 'HH:MM' -> '9a' / '1:30p' (compact, for chip subtitles)
function fmtHM12(hm) {
  if (!hm) return '';
  const [h, m] = String(hm).split(':').map(Number);
  const ap = h >= 12 ? 'p' : 'a';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m ? `${h12}:${String(m).padStart(2, '0')}${ap}` : `${h12}${ap}`;
}

// Today-page label for why a driver is off (from /drivers/available's
// availabilityNote). Flex drivers with no entry are UNKNOWN, never "off".
function offLabel(note) {
  if (!note) return 'off';
  if (note.startsWith('appointment ')) return 'out ' + note.replace('appointment ', '').split('-').map(fmtHM12).join('–');
  if (note === 'day_off' || note === 'call_in' || note === 'unavailable') return note.replace('_', ' ');
  if (note === 'pattern_off') return 'no entry';
  return note.replace(/_/g, ' ');
}

function DriverAvailablePanel({ drivers, transports }) {
  const now = new Date();
  // One-tap "done for day" (Paula 2026-07-16). Optimistic local override so
  // the chip flips instantly; the 30s fetchAll cycle reconciles with the
  // server (same done_for_day flag the driver portal sets).
  const [homeOverride, setHomeOverride] = useState({}); // driverId -> true (sent) / false (undone)
  const markDone = async (d) => {
    setHomeOverride(prev => ({ ...prev, [d.id]: true }));
    try { await api.post(`/drivers/${d.id}/done-for-day`); } catch (e) { setHomeOverride(prev => ({ ...prev, [d.id]: undefined })); }
  };
  const undoDone = async (d) => {
    setHomeOverride(prev => ({ ...prev, [d.id]: false }));
    try { await api.delete(`/drivers/${d.id}/done-for-day`); } catch (e) { setHomeOverride(prev => ({ ...prev, [d.id]: undefined })); }
  };
  const isHome = (d) => homeOverride[d.id] !== undefined ? homeOverride[d.id] : !!d.doneForDay;

  // Build map: driver → their trips TODAY only (not future days)
  const todayStr = todayET();
  const driverTrips = {};
  transports.filter(t => {
    if (['cancelled', 'completed'].includes(t.status)) return false;
    // Only include trips scheduled for today
    if (!t.scheduled_time) return false;
    const tripDate = format(toET(t.scheduled_time), 'yyyy-MM-dd');
    return tripDate === todayStr;
  }).forEach(t => {
    (t.assignees || []).forEach(a => {
      const did = a.driverId || a.driver_id;
      if (!driverTrips[did]) driverTrips[did] = [];
      driverTrips[did].push(t);
    });
  });

  // Only show drivers who have trips dispatched TODAY
  // Skip on-call people who aren't actively working
  const activeDrivers = drivers.filter(d => {
    if (!d.isAvailable) return false;
    // Hide drivers who marked themselves done for the day (today only)
    if (isHome(d)) return false;
    const hasTripsToday = (driverTrips[d.id] || []).length > 0;
    const isOnCall = ON_CALL_IDS.has(d.id) || d.isOnCall;
    // Show if they have trips today, OR they're NOT on-call
    return hasTripsToday || !isOnCall;
  });

  if (activeDrivers.length === 0) return null;

  const driverAvail = activeDrivers.map(d => {
    const trips = (driverTrips[d.id] || []).sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time));

    const win = computeFreeWindow(trips, now);
    const isOnTrip = trips.some(t => ['en_route_pickup', 'picked_up', 'dropped_off'].includes(t.status));
    const isFreeNow = !isOnTrip && !win.busyNow;
    const isDriving = d.hos?.isDrivingNow;
    const doneCount = trips.filter(t => t.status === 'completed').length;

    return { driver: d, nextFree: win.freeAt, freeUntil: win.freeUntil, isFreeNow, isOnTrip, isDriving, tripCount: doneCount };
  });

  driverAvail.sort((a, b) => {
    if (a.isFreeNow && !b.isFreeNow) return -1;
    if (!a.isFreeNow && b.isFreeNow) return 1;
    return (a.nextFree || 0) - (b.nextFree || 0);
  });

  return (
    <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 10, padding: '10px 14px' }}>
      <div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
        Drivers — next available
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {driverAvail.map(da => (
          <div key={da.driver.id} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '5px 10px', borderRadius: 8,
            background: da.isFreeNow ? '#E1F5EE' : (da.isDriving || da.isOnTrip) ? '#FAEEDA' : '#f5f5f3',
            border: `0.5px solid ${da.isFreeNow ? '#5DCAA5' : (da.isDriving || da.isOnTrip) ? '#FAC775' : '#e0e0db'}`,
            fontSize: 11,
          }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              background: da.isFreeNow ? '#0F6E56' : (da.isDriving || da.isOnTrip) ? '#BA7517' : '#999',
            }} />
            <div>
              <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
                {da.driver.firstName}
                {da.driver.atOffice && (
                  <span style={{
                    fontSize: 9, fontWeight: 600, color: '#0F6E56', background: '#D0F0E4',
                    padding: '1px 5px', borderRadius: 4, lineHeight: '14px',
                  }}>@ office</span>
                )}
              </div>
              <div style={{ fontSize: 10, color: da.isFreeNow ? '#0F6E56' : '#666' }}>
                {da.isFreeNow
                  ? `Available${da.freeUntil ? ` until ${format(toET(da.freeUntil), 'h:mm a')}` : ''}${da.tripCount > 0 ? ` · ${da.tripCount} done` : ''}`
                  : da.nextFree
                    ? `Busy · free ~${format(toET(da.nextFree), 'h:mm a')}`
                    : 'Available'
                }
                {/* Today's window + any partial-day block, so nobody has to
                    flip to the Schedule page (Paula 2026-09-01) */}
                {da.driver.scheduleType === 'flexible' && da.driver.scheduledWindow?.from && (
                  <span style={{ color: '#888' }}> · {fmtHM12(da.driver.scheduledWindow.from)}–{fmtHM12(da.driver.scheduledWindow.until)}</span>
                )}
                {(da.driver.availabilityNote || '').startsWith('appointment ') && (
                  <span style={{ color: '#A32D2D', fontWeight: 600 }}> · {offLabel(da.driver.availabilityNote)}</span>
                )}
              </div>
            </div>
            <button
              onClick={() => markDone(da.driver)}
              title={`Mark ${da.driver.firstName} done for the day`}
              style={{
                border: 'none', background: 'transparent', cursor: 'pointer',
                fontSize: 13, padding: '2px 2px 2px 4px', opacity: 0.45, lineHeight: 1,
              }}
            >🏠</button>
          </div>
        ))}
      </div>
      {(() => {
        const sentHome = drivers.filter(d => d.status !== 'inactive' && isHome(d));
        if (sentHome.length === 0) return null;
        return (
          <div style={{ fontSize: 10, color: '#aaa', marginTop: 6 }}>
            Done for day: {sentHome.map(d => (
              <span key={d.id} style={{ marginRight: 8 }}>
                {d.firstName}
                <button onClick={() => undoDone(d)} style={{
                  border: 'none', background: 'transparent', color: '#185FA5',
                  cursor: 'pointer', fontSize: 10, padding: '0 0 0 3px', textDecoration: 'underline',
                }}>undo</button>
              </span>
            ))}
          </div>
        );
      })()}
      {(() => {
        // Who is OFF today and why — the other half of "stop flipping to the
        // Schedule page". On-call crew live in their own box; done-for-day
        // is listed above.
        // Only APPROVED time off (day off / call-in / partial-day out) --
        // not set-schedule guys on their normal day off, not flex drivers
        // with no entry (Paula 2026-09-01).
        const TIME_OFF_NOTES = ['day_off', 'call_in', 'unavailable', 'leave', 'training', 'partial_day_off'];
        const off = drivers.filter(d => d.status !== 'inactive' && !d.isAvailable && !isHome(d)
          && !(ON_CALL_IDS.has(d.id) || d.isOnCall)
          && (TIME_OFF_NOTES.includes(d.availabilityNote) || (d.availabilityNote || '').startsWith('appointment ')));
        if (off.length === 0) return null;
        return (
          <div style={{ fontSize: 10, color: '#aaa', marginTop: 6 }}>
            Off today: {off.map(d => (
              <span key={d.id} style={{ marginRight: 8 }}>
                <span style={{ color: '#888', fontWeight: 500 }}>{d.firstName}</span>
                <span style={{ color: '#bbb' }}> ({offLabel(d.availabilityNote)})</span>
              </span>
            ))}
          </div>
        );
      })()}
    </div>
  );
}

// On-call crew gets its own box under the availability panels, blue and
// readable (was a 10px gray footnote inside the drivers panel). An on-call
// driver actively working today shows in the drivers panel instead.
function OnCallPanel({ drivers, transports }) {
  const todayStr = todayET();
  const workingToday = new Set();
  transports.filter(t => {
    if (['cancelled', 'completed'].includes(t.status)) return false;
    if (!t.scheduled_time) return false;
    return format(toET(t.scheduled_time), 'yyyy-MM-dd') === todayStr;
  }).forEach(t => {
    (t.assignees || []).forEach(a => workingToday.add(a.driverId || a.driver_id));
  });

  const onCall = drivers.filter(d => {
    if (d.status === 'inactive') return false;
    if (!(ON_CALL_IDS.has(d.id) || d.isOnCall)) return false;
    return !workingToday.has(d.id);
  });
  if (onCall.length === 0) return null;

  // 'HH:MM' ET window strings -> "9:30 PM"; label whether the window is
  // active now (Paula 2026-08-25: Scott/Dejan/Brankica come on LATER —
  // the box must not read as "available now")
  const fmtHM = (hm) => {
    const [h, m] = hm.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return m === 0 ? `${h12} ${ampm}` : `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  };
  const nowHM = format(toET(new Date()), 'HH:mm');
  const windowState = (d) => {
    const w = d.scheduledWindow;
    if (!w || !w.from) return { label: 'on call', active: false };
    // Overnight windows wrap midnight (e.g. 21:30-07:00)
    const wraps = w.until && w.until < w.from;
    const active = wraps
      ? (nowHM >= w.from || nowHM < w.until)
      : (nowHM >= w.from && (!w.until || nowHM < w.until));
    if (active) return { label: 'on now', active: true };
    if (nowHM < w.from) return { label: `from ${fmtHM(w.from)}`, active: false };
    return { label: 'done today', active: false };
  };

  return (
    <div style={{ background: '#EBF3FB', border: '0.5px solid #A8C8E8', borderRadius: 10, padding: '10px 14px', marginBottom: 8 }}>
      <div style={{ fontSize: 11, color: '#185FA5', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
        On call
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {onCall.map(d => {
          const ws = windowState(d);
          return (
            <div key={d.id} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 12px', borderRadius: 8,
              background: 'white', border: '0.5px solid #A8C8E8',
              opacity: ws.active ? 1 : 0.75,
            }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: ws.active ? '#185FA5' : '#B9CFE4' }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0C447C' }}>{d.firstName}</div>
                <div style={{ fontSize: 10, color: ws.active ? '#185FA5' : '#7A94AC' }}>{ws.label}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Today's money (Paula 2026-09-24): billed vs crew pay for the day, owners
// only. Rendered for role 'admin' (Paula, Chance); Erika is 'dispatcher' and
// the server refuses her too. No patient data on this panel.
const currentUser = (() => { try { return JSON.parse(localStorage.getItem('nemt_user') || '{}'); } catch (e) { return {}; } })();

function DayMoneyPanel() {
  const isMobile = window.matchMedia('(max-width: 700px)').matches;
  const [date, setDate] = useState(todayET());
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(() => { try { return localStorage.getItem('nemt_day_money_open') !== '0'; } catch (e) { return true; } });
  const [showTrips, setShowTrips] = useState(false);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await api.get(`/billing/day?date=${date}`);
        if (alive) { setData(res.data); setErr(false); }
      } catch (e) { if (alive) setErr(true); }
    };
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, [date]);

  const shiftDay = (n) => {
    const [y, m, d] = date.split('-').map(Number);
    const dt = new Date(y, m - 1, d + n);
    setDate(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`);
  };
  const toggle = () => { setOpen(o => { try { localStorage.setItem('nemt_day_money_open', o ? '0' : '1'); } catch (e) {} return !o; }); };
  const money = v => '$' + (parseFloat(v || 0) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const isToday = date === todayET();
  const [y, m, d] = date.split('-').map(Number);
  const label = isToday ? 'Today' : format(new Date(y, m - 1, d), 'EEE M/d');

  if (err) return null;
  const T = data ? data.totals : null;

  return (
    <div style={{ background: '#fff', border: '1px solid #C9DDF2', borderRadius: 10, marginBottom: 12, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#EDF4FB', cursor: 'pointer' }} onClick={toggle}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#185FA5' }}>
          {label}'s money
          {T && !open && <span style={{ marginLeft: 10, color: '#333' }}>{money(T.billed)} billed, {money(T.driver_pay)} pay</span>}
        </div>
        <span style={{ fontSize: 11, color: '#185FA5' }}>{open ? 'Hide' : 'Show'}</span>
      </div>
      {open && (
        <div style={{ padding: '10px 12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <button onClick={() => shiftDay(-1)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 12 }}>&lsaquo; Prev</button>
            <span style={{ fontSize: 12, color: '#555', flex: 1, textAlign: 'center' }}>{isToday ? 'Today, ' : ''}{format(new Date(y, m - 1, d), 'EEEE, MMMM d')}</span>
            <button onClick={() => shiftDay(1)} disabled={isToday} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', cursor: isToday ? 'default' : 'pointer', fontSize: 12, opacity: isToday ? 0.4 : 1 }}>Next &rsaquo;</button>
          </div>
          {!T ? <div style={{ color: '#999', fontSize: 12 }}>Loading...</div> : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 8, marginBottom: 8 }}>
                {[
                  { label: 'Billed', value: money(T.billed), color: '#185FA5', sub: T.trips + ' trip' + (T.trips === 1 ? '' : 's') + (T.cancelled_with_fee ? ', ' + T.cancelled_with_fee + ' cancel fee' + (T.cancelled_with_fee === 1 ? '' : 's') : '') },
                  { label: 'Driver pay', value: money(T.driver_pay), color: '#BA7517', sub: T.completed + ' done' },
                  { label: 'Margin', value: money(T.margin), color: T.margin >= 0 ? '#0F6E56' : '#A32D2D', sub: T.billed > 0 ? Math.round(T.margin / T.billed * 100) + '%' : '--' },
                ].map(s => (
                  <div key={s.label} style={{ background: '#f5f5f3', borderRadius: 8, padding: '8px 10px' }}>
                    <div style={{ fontSize: 10, color: '#999' }}>{s.label}</div>
                    <div style={{ fontSize: isMobile ? 17 : 20, fontWeight: 600, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: 10, color: '#888' }}>{s.sub}</div>
                  </div>
                ))}
              </div>
              {(T.still_to_run > 0 || T.in_progress > 0 || T.done_unbilled > 0) && (
                <div style={{ fontSize: 11, color: '#666', marginBottom: 6 }}>
                  {T.in_progress > 0 && <span style={{ marginRight: 10 }}>{T.in_progress} on the road</span>}
                  {T.still_to_run > 0 && <span style={{ marginRight: 10 }}>{T.still_to_run} still to run (not billed yet)</span>}
                  {T.done_unbilled > 0 && <span style={{ color: '#A32D2D', fontWeight: 600 }}>{T.done_unbilled} completed with no billing record</span>}
                </div>
              )}
              {T.pushed > 0 && (
                <div style={{ fontSize: 11, color: '#854F0B', marginBottom: 6 }}>
                  {T.pushed} trip{T.pushed === 1 ? '' : 's'} pushed later than asked (avg {Math.round(T.pushed_minutes / T.pushed)} min)
                </div>
              )}
              {data.facilities.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                  {data.facilities.map(f => (
                    <span key={f.name} style={{ fontSize: 11, background: '#EDF4FB', color: '#185FA5', borderRadius: 12, padding: '3px 10px' }}>{f.name} {money(f.amount)}</span>
                  ))}
                </div>
              )}
              <a href="#" onClick={e => { e.preventDefault(); setShowTrips(v => !v); }} style={{ fontSize: 11, color: '#185FA5' }}>{showTrips ? 'Hide trips' : 'Show trips (' + data.trips.length + ')'}</a>
              {showTrips && (
                <div style={{ marginTop: 6, fontSize: 11 }}>
                  {data.trips.map(t => {
                    const cancelled = t.status === 'cancelled' || t.status === 'no_show';
                    return (
                      <div key={t.transport_number} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '5px 0', borderBottom: '1px solid #f0f0f0', color: cancelled ? '#999' : '#333' }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <span style={{ fontWeight: 600, color: cancelled ? '#999' : '#185FA5' }}>{t.transport_number.replace('TRP-2026-', '')}</span>
                          <span style={{ marginLeft: 6, color: '#888' }}>{t.scheduled_time ? format(toET(t.scheduled_time), 'h:mm a') : ''}</span>
                          <span style={{ marginLeft: 6 }}>{t.facility || ''}</span>
                          <span style={{ marginLeft: 6, color: '#777', overflowWrap: 'anywhere' }}>{(t.dest || '').slice(0, 28)}</span>
                          {cancelled && <span style={{ marginLeft: 6, fontStyle: 'italic' }}>{t.status.replace('_', ' ')}</span>}
                          {t.invoice_number && <span style={{ marginLeft: 6, color: '#0F6E56' }}>inv {t.invoice_number}</span>}
                          {t.pushed_min && <span style={{ marginLeft: 6, color: '#854F0B' }}>pushed {t.pushed_min} min</span>}
                        </div>
                        <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <div style={{ fontWeight: 600 }}>{t.billed === null ? (t.status === 'completed' ? <span style={{ color: '#A32D2D' }}>no record</span> : <span style={{ color: '#bbb' }}>--</span>) : money(t.billed)}</div>
                          <div style={{ color: '#BA7517' }}>{t.driver_pay > 0 ? money(t.driver_pay) + (t.crew ? ' ' + t.crew : '') : ''}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function DriversTodayPanel({ drivers }) {
  const available = drivers.filter(d => d.isAvailable);
  if (available.length === 0) return null;

  return (
    <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 10, padding: '10px 14px', marginBottom: 16 }}>
      <div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
        Drivers today ({available.length}) — today hrs / week hrs
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {available
          .sort((a, b) => {
            const aDriving = a.hos?.isDrivingNow ? 1 : 0;
            const bDriving = b.hos?.isDrivingNow ? 1 : 0;
            if (bDriving !== aDriving) return bDriving - aDriving;
            return (b.weeklyStats?.hoursThisWeek || 0) - (a.weeklyStats?.hoursThisWeek || 0);
          })
          .map(d => {
            const todayHrs = parseFloat(d.weeklyStats?.hoursToday || 0).toFixed(2);
            const weekHrs = parseFloat(d.weeklyStats?.hoursThisWeek || 0).toFixed(2);
            const isDriving = d.hos?.isDrivingNow;
            const hasActivity = parseFloat(todayHrs) > 0 || parseFloat(weekHrs) > 0;
            return (
              <div key={d.id} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '4px 8px', borderRadius: 20,
                background: isDriving ? '#E1F5EE' : hasActivity ? '#f5f5f3' : '#FAFAF9',
                border: `0.5px solid ${isDriving ? '#5DCAA5' : '#e0e0db'}`,
                fontSize: 11,
              }}>
                <span style={{ fontWeight: 500, color: hasActivity ? '#333' : '#999' }}>{d.firstName}</span>
                {d.atOffice && <span style={{ fontSize: 8, color: '#0F6E56', fontWeight: 600 }}>@</span>}
                {hasActivity ? (
                  <>
                    <span style={{ color: '#185FA5' }}>{todayHrs}</span>
                    <span style={{ color: '#aaa' }}>/</span>
                    <span style={{ color: '#666' }}>{weekHrs}w</span>
                  </>
                ) : (
                  <span style={{ color: '#bbb' }}>--</span>
                )}
                {isDriving && <span style={{ fontSize: 8, color: '#0F6E56' }}>⏱️</span>}
              </div>
            );
          })}
      </div>
    </div>
  );
}

function VehicleCard({ vehicle, trips, isRoundTripPart, onAssign, onEdit, onComplete, onCancel, onRefresh }) {
  const vehicleTrips = trips.filter(t =>
    t.vehicle_id === vehicle.id &&
    !['cancelled', 'completed', 'pending_approval'].includes(t.status)
  );
  const isOut = vehicle.status !== 'active';
  const dot = PLATE_DOTS[vehicle.license_plate] || '#888';

  return (
    <div style={{ background: 'white', border: `0.5px solid ${isOut ? '#FAC775' : '#e0e0db'}`, borderRadius: 12, marginBottom: 10, overflow: 'hidden', opacity: isOut ? 0.7 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '0.5px solid #e0e0db', background: isOut ? '#FAEEDA22' : '#f9f9f8' }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: dot, flexShrink: 0 }} />
        <span style={{ fontWeight: 500, fontSize: 13, flex: 1 }}>{vehicle.name}</span>
        <span style={{ fontSize: 11, color: '#aaa' }}>{vehicle.license_plate}</span>
        <Badge
          bg={vehicle.vehicle_type === 'sedan' ? '#E6F1FB' : '#EEEDFE'}
          color={vehicle.vehicle_type === 'sedan' ? '#0C447C' : '#3C3489'}
          label={vehicle.vehicle_type === 'sedan' ? 'Sedan' : 'Wheelchair'}
        />
        {isOut && <Badge bg="#FAEEDA" color="#854F0B" label="Out of service" />}
      </div>

      {isOut ? (
        <div style={{ padding: '10px 14px', fontSize: 12, color: '#854F0B' }}>{vehicle.notes || 'Out of service'}</div>
      ) : vehicleTrips.length === 0 ? (
        <div style={{ padding: '10px 14px', fontSize: 12, color: '#aaa', fontStyle: 'italic' }}>No trips scheduled</div>
      ) : (
        <div style={{ padding: '10px 14px' }}>
          {vehicleTrips.map(trip => <TripCard key={trip.id} trip={trip} isRoundTrip={isRoundTripPart ? isRoundTripPart(trip) : false} onAssign={onAssign} onEdit={onEdit} onComplete={onComplete} onCancel={onCancel} onRefresh={onRefresh} />)}
        </div>
      )}
    </div>
  );
}

export default function Today() {
  const [transports, setTransports] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [currentlyDriving, setCurrentlyDriving] = useState([]);
  const [liveLocations, setLiveLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [minDriverWarning, setMinDriverWarning] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [assigningTrip, setAssigningTrip] = useState(null);
  const [rejectingTrip, setRejectingTrip] = useState(null);
  const [editingTrip, setEditingTrip] = useState(null);
  const [mergingTrip, setMergingTrip] = useState(null);
  const [showPriorities, setShowPriorities] = useState(false);
  const [showRunRosie, setShowRunRosie] = useState(false);
  const [rosieProposals, setRosieProposals] = useState(null);
  const [rosieRunning, setRosieRunning] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const [cancelledTrips, setCancelledTrips] = useState([]);
  const [mergeTargets, setMergeTargets] = useState([]);

  // When merge modal opens, fetch all recent trips (not just today)
  useEffect(() => {
    if (!mergingTrip) { setMergeTargets([]); return; }
    (async () => {
      try {
        // Fetch today, tomorrow, and pending trips
        const today = todayET();
        const tomorrow = format(toET(Date.now() + 86400000), 'yyyy-MM-dd');
        const [todayRes, tomorrowRes, pendingRes] = await Promise.all([
          api.get(`/transports?date=${today}`),
          api.get(`/transports?date=${tomorrow}`),
          api.get('/transports?status=pending_approval'),
        ]);
        const seen = new Set();
        const all = [];
        [...(todayRes.data.transports || []), ...(tomorrowRes.data.transports || []), ...(pendingRes.data.transports || [])].forEach(t => {
          if (!seen.has(t.id)) { seen.add(t.id); all.push(t); }
        });
        setMergeTargets(all);
      } catch (e) { setMergeTargets(transports); }
    })();
  }, [mergingTrip]);

  const handleMerge = async (keepId, duplicateId) => {
    try {
      await api.post(`/transports/${keepId}/merge`, { duplicateId });
      setMergingTrip(null);
      fetchAll();
    } catch (err) {
      alert('Merge failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleAdminComplete = async (trip) => {
    if (!window.confirm(`Complete ${trip.transport_number}? Billing and payroll will be calculated from estimated times.`)) return;
    try {
      await api.put(`/transports/${trip.id}/admin-complete`);
      fetchAll();
    } catch (err) {
      alert('Could not complete trip: ' + (err.response?.data?.error || err.message));
    }
  };

  const prevPendingIdsRef = useRef(null);
  const prevWillCallIdsRef = useRef(null);
  const prevPatientReadyIdsRef = useRef(null);
  const today = todayET();

  const fetchAll = async () => {
    try {
      const [todayRes, pendingRes, willCallRes, inProgressRes, vRes, dRes, cdRes] = await Promise.all([
        api.get(`/transports?date=${today}`),
        api.get(`/transports?status=pending_approval`),
        api.get(`/transports?willCall=1`),
        api.get('/transports?inProgress=1'),
        api.get('/vehicles'),
        api.get(`/drivers/available?date=${today}&time=${new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York' })}`),
        api.get('/drivers/currently-driving'),
      ]);
      setCurrentlyDriving(cdRes.data.active || []);
      // Fetch live GPS locations
      try {
        const gpsRes = await api.get('/drivers/live-locations');
        setLiveLocations(gpsRes.data.locations || []);
      } catch (e) {}
      // Merge today's trips with pending trips and any open will-call legs, deduping by id.
      const merged = [];
      const seen = new Set();
      const pushAll = (arr) => (arr || []).forEach(t => {
        if (!seen.has(t.id)) { seen.add(t.id); merged.push(t); }
      });
      pushAll(todayRes.data.transports);
      // Show pending_approval from any future date (need to accept/reject them now)
      // Once approved/dispatched, they disappear from today and show on their actual date
      const todayStr = todayET();
      pushAll((pendingRes.data.transports || []).filter(t => {
        if (t.status !== 'pending_approval') return false;
        return true;
      }));
      pushAll(willCallRes.data.transports);
      pushAll(inProgressRes.data.transports);
      setTransports(merged);
      setVehicles(vRes.data.vehicles);
      setDrivers([...dRes.data.available, ...dRes.data.unavailable]);
      setMinDriverWarning(dRes.data.minDriverWarning || null);

      // Alert on new pending trips, new will-call trips, or patient-ready events
      const pendingIds = merged.filter(t => t.status === 'pending_approval').map(t => t.id);
      const willCallIds = merged.filter(t => t.is_will_call && !['completed','cancelled'].includes(t.status)).map(t => t.id);
      const patientReadyIds = merged.filter(t => t.is_will_call && t.patient_ready_at).map(t => t.id);
      if (prevPendingIdsRef.current !== null) {
        const newPending = pendingIds.filter(id => !prevPendingIdsRef.current.has(id));
        const newWillCall = willCallIds.filter(id => !prevWillCallIdsRef.current.has(id));
        const newPatientReady = patientReadyIds.filter(id => !prevPatientReadyIdsRef.current.has(id));
        if (newPending.length > 0 || newWillCall.length > 0 || newPatientReady.length > 0) playDispatchAlert();
      }
      prevPendingIdsRef.current = new Set(pendingIds);
      prevWillCallIdsRef.current = new Set(willCallIds);
      prevPatientReadyIdsRef.current = new Set(patientReadyIds);
    } catch (err) {
      setError('Could not load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // Auto-poll every 30 seconds for new trips + alerts
    const interval = setInterval(fetchAll, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleApproveOnly = async (trip) => {
    try {
      if (!(await approveTrip(trip.id))) return;
      fetchAll();
    } catch (err) {
      alert('Could not approve trip');
    }
  };

  const handleApproveDispatch = async (trip) => {
    try {
      const proposedDrivers = typeof trip.proposed_drivers === 'string'
        ? JSON.parse(trip.proposed_drivers) : trip.proposed_drivers;
      if (!proposedDrivers || proposedDrivers.length === 0 || !trip.proposed_vehicle_id) {
        alert('No proposed dispatch to approve');
        return;
      }
      // Approve trip first (if pending), then assign the proposed drivers + vehicle
      if (trip.status === 'pending_approval') {
        if (!(await approveTrip(trip.id))) return;
      }
      await api.post(`/transports/${trip.id}/assign`, {
        assignments: proposedDrivers.map(d => ({
          driverId: d.driverId,
          role: d.role,
          hourlyRate: 40,
        })),
        vehicleId: trip.proposed_vehicle_id,
        forceAssign: true,
      });
      fetchAll();
    } catch (err) {
      if (err.response?.data?.requiresSplitConfirm) {
        alert(err.response.data.message + '\n\nOpen the trip and use Assign if you really want a second driver.');
        return;
      }
      alert('Could not approve dispatch: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleReject = async (reason, rescheduleTime, prepayAcknowledged, includeSibling) => {
    try {
      const res = await api.put(`/transports/${rejectingTrip.id}/cancel`, {
        reason: reason || 'Cancelled by admin',
        rescheduleTime: rescheduleTime || null,
        prepayAcknowledged: !!prepayAcknowledged,
        includeSibling: !!includeSibling,
      });
      if (res.data.cancellationBilled) {
        alert(`Cancellation billed. ${res.data.rescheduledTrip ? 'New trip created at the rescheduled time.' : ''}`);
      }
      setRejectingTrip(null);
      fetchAll();
    } catch (err) {
      // 409 = quoted/prepaid booking: show the money warning, retry acknowledged
      if (err.response?.status === 409 && err.response.data?.requiresPrepayAck) {
        if (window.confirm(err.response.data.warning)) {
          return handleReject(reason, rescheduleTime, true);
        }
        return;
      }
      alert('Could not cancel trip');
    }
  };

  // Open will-call legs — show in their own section and exclude from the main needs-attention list.
  const willCallLegs = transports.filter(t => {
    if (!t.is_will_call || ['completed', 'cancelled'].includes(t.status)) return false;
    // Future will-calls that are pending_approval show in the pending section, not here
    if (t.status === 'pending_approval' && t.scheduled_time) {
      const tripDate = format(toET(t.scheduled_time), 'yyyy-MM-dd');
      if (tripDate > today) return false;
    }
    return true;
  });
  const willCallIds = new Set(willCallLegs.map(t => t.id));

  const pending = transports.filter(t => t.status === 'pending_approval' && !willCallIds.has(t.id));
  const needsAssignment = transports.filter(t => t.status === 'approved' && !willCallIds.has(t.id));
  const needsAttention = [...pending, ...needsAssignment];
  const active = transports.filter(t => !['pending_approval', 'cancelled', 'completed'].includes(t.status));
  const activeVehicles = vehicles.filter(v => v.status === 'active');

  // Ids of trips that are outbound legs of a round trip (someone else points to them).
  const outboundLegIds = new Set(
    transports.filter(t => t.return_leg_of).map(t => t.return_leg_of)
  );
  const isRoundTripPart = (trip) => !!trip.return_leg_of || outboundLegIds.has(trip.id);

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 500, margin: 0 }}>Today's dispatch</h1>
          <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{format(toET(new Date()), 'EEEE, MMMM d, yyyy')}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={fetchAll} style={{ fontSize: 12 }}>Refresh</button>
          <button onClick={() => setShowPriorities(true)} style={{ fontSize: 12, padding: '6px 14px' }}>
            Set priorities
          </button>
          <button disabled={rosieRunning} onClick={async () => {
            setRosieRunning(true);
            try {
              const res = await api.post('/transports/run-rosie-run', { date: today });
              setRosieProposals(res.data);
              setShowRunRosie(true);
            } catch (err) { alert('Run Rosie Run failed: ' + (err.response?.data?.error || err.message)); }
            finally { setRosieRunning(false); }
          }} style={{ fontSize: 12, padding: '6px 14px', background: '#1D9E75', color: 'white', border: 'none', fontWeight: 600 }}>
            {rosieRunning ? 'Running...' : 'Run Rosie Run'}
          </button>
          <button onClick={() => setShowCreateModal(true)} style={{ fontSize: 12, background: '#185FA5', color: 'white', border: '0.5px solid #185FA5', padding: '6px 14px' }}>
            + New trip
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 8, marginBottom: 8 }}>
        {[
          { label: 'Total trips', value: transports.filter(t => t.status !== 'cancelled').length, color: '#185FA5' },
          { label: 'In progress', value: transports.filter(t => ['en_route_pickup','picked_up','dropped_off'].includes(t.status)).length, color: '#1D9E75' },
          { label: 'To be completed', value: transports.filter(t => ['approved','dispatched'].includes(t.status)).length, color: '#185FA5' },
          { label: 'Completed', value: transports.filter(t => t.status === 'completed').length, color: '#666' },
        ].map(s => (
          <div key={s.label} style={{ background: '#f5f5f3', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: 10, color: '#999', marginBottom: 3 }}>{s.label}</div>
            <div style={{ fontSize: 20, fontWeight: 500, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>
      {/* Today's money -- owners only (Paula, Chance). Erika never sees it. */}
      {currentUser.role === 'admin' && <DayMoneyPanel />}

      {/* Next Available panels */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 4 }}>
        <NextAvailablePanel vehicles={vehicles} transports={transports} drivers={drivers} />
        <DriverAvailablePanel drivers={drivers} transports={transports} />
      </div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 8, fontSize: 10, color: '#aaa' }}>
        <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#0F6E56', marginRight: 3 }}></span>Available now</span>
        <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#BA7517', marginRight: 3 }}></span>On a trip</span>
        <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#999', marginRight: 3 }}></span>Has upcoming trip</span>
      </div>

      {/* On-call crew — separate blue box below the availability panels */}
      <OnCallPanel drivers={drivers} transports={transports} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 8, marginBottom: 16 }}>
        {[
          { label: 'Needs attention', value: needsAttention.length, color: needsAttention.length > 0 ? '#BA7517' : '#666' },
          { label: 'Drivers active', value: drivers.filter(d => d.isAvailable).length, color: '#1D9E75' },
          { label: 'Drivers on-call', value: drivers.filter(d => d.isOnCall).length, color: '#185FA5' },
        ].map(s => (
          <div key={s.label} style={{ background: '#f5f5f3', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: 10, color: '#999', marginBottom: 3 }}>{s.label}</div>
            <div style={{ fontSize: 20, fontWeight: 500, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Drivers available / on call today */}
      <DriversTodayPanel drivers={drivers} />

      {minDriverWarning && (
        <div style={{
          background: '#FCEBEB', border: '1px solid #F09595', borderRadius: 10,
          padding: '10px 14px', marginBottom: 16,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 18 }}>⚠️</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: '#A32D2D' }}>{minDriverWarning.message}</div>
            <div style={{ fontSize: 11, color: '#A32D2D', marginTop: 2 }}>
              Consider calling in additional drivers or adjusting the schedule.
            </div>
          </div>
        </div>
      )}

      <LiveMapPanel locations={liveLocations} />

      {currentlyDriving.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8, color: '#0F6E56' }}>
            ⏱️ Currently driving ({currentlyDriving.length})
          </div>
          <div style={{ background: 'white', border: '0.5px solid #5DCAA5', borderRadius: 10, padding: '10px 14px' }}>
            {currentlyDriving.map((seg, i) => (
              <div key={seg.id} style={{
                padding: '8px 0',
                borderBottom: i < currentlyDriving.length - 1 ? '0.5px solid #f0f0f0' : 'none',
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{seg.driver_name}</div>
                  <div style={{ fontSize: 11, color: '#666', marginTop: 1 }}>
                    {seg.vehicle_name || 'Vehicle TBD'}
                    {seg.transport_number ? ` · ${seg.transport_number}` : ''}
                    {seg.destination_address ? ` → ${seg.destination_address}` : ''}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#0F6E56' }}>
                    {seg.minutes_driving}m
                  </div>
                  <div style={{ fontSize: 10, color: '#999' }}>in segment</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {willCallLegs.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8, color: '#854F0B', display: 'flex', alignItems: 'center', gap: 8 }}>
            📞 Will call — waiting for patient to call
            {willCallLegs.filter(t => t.patient_ready_at).length > 0 && (
              <span style={{ fontSize: 11, fontWeight: 600, background: '#1D9E75', color: 'white', padding: '2px 8px', borderRadius: 20 }}>
                {willCallLegs.filter(t => t.patient_ready_at).length} ready!
              </span>
            )}
          </div>
          {willCallLegs.map(trip => (
            <WillCallCard
              key={trip.id} trip={trip}
              onEdit={setEditingTrip}
              onCancel={setRejectingTrip}
              onApprove={handleApproveOnly}
            />
          ))}
        </div>
      )}

      {needsAttention.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Needs attention</div>
          {needsAttention.map(trip => (
            <PendingTripCard
              key={trip.id} trip={trip}
              vehicles={activeVehicles} drivers={drivers}
              existingTransports={transports}
              isRoundTrip={isRoundTripPart(trip)}
              onApprove={handleApproveOnly}
              onReject={setRejectingTrip}
              onAssign={setAssigningTrip}
              onEdit={setEditingTrip}
              onMerge={setMergingTrip}
              onApproveDispatch={handleApproveDispatch}
            />
          ))}
        </div>
      )}

      {loading ? (
        <div style={{ color: '#999', fontSize: 13 }}>Loading...</div>
      ) : error ? (
        <div style={{ color: '#A32D2D', fontSize: 13 }}>{error}</div>
      ) : (
        <div>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Vehicle board</div>
          {/* Retired vehicles never render a board card (Gray WC van totaled 8/18, Paula 8/19) */}
          {vehicles.filter(v => v.status !== 'retired').map(vehicle => (
            <VehicleCard key={vehicle.id} vehicle={vehicle} trips={transports} isRoundTripPart={isRoundTripPart} onAssign={setAssigningTrip} onEdit={setEditingTrip} onComplete={handleAdminComplete} onCancel={setRejectingTrip} onRefresh={fetchAll} />
          ))}
        </div>
      )}

      {/* Cancelled trips — toggle to show/restore */}
      <div style={{ marginTop: 16 }}>
        <button onClick={async () => {
          if (!showCancelled) {
            try {
              const res = await api.get(`/transports?date=${today}&status=cancelled`);
              setCancelledTrips((res.data.transports || []).filter(t => t.status === 'cancelled'));
            } catch (e) {}
          }
          setShowCancelled(!showCancelled);
        }} style={{ fontSize: 11, color: '#999', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          {showCancelled ? '▲ Hide cancelled trips' : '▼ Show cancelled trips'}
        </button>
        {showCancelled && cancelledTrips.length > 0 && (
          <div style={{ marginTop: 8 }}>
            {cancelledTrips.map(t => {
              const time = t.scheduled_time ? format(toET(t.scheduled_time), 'h:mm a') : '--';
              return (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', marginBottom: 4, background: '#f9f9f8', borderRadius: 6, opacity: 0.7 }}>
                  <div>
                    <span style={{ fontSize: 12, fontWeight: 500, color: '#999' }}>{time}</span>
                    <span style={{ fontSize: 11, color: '#999', marginLeft: 8 }}>{t.transport_number}</span>
                    <span style={{ fontSize: 12, color: '#888', marginLeft: 8 }}>{(t.destination_address || '').substring(0, 35)}</span>
                  </div>
                  <button onClick={async () => {
                    try {
                      try {
                        await api.put(`/transports/${t.id}/admin-status`, { status: 'approved' });
                      } catch (e) {
                        // Unpaid self-booking (pay-before-accept): explicit OK required
                        if (e.response?.status === 409 && e.response.data?.requiresPaymentAck) {
                          if (!window.confirm(e.response.data.message)) return;
                          await api.put(`/transports/${t.id}/admin-status`, { status: 'approved', acceptUnpaid: true });
                        } else throw e;
                      }
                      setCancelledTrips(prev => prev.filter(ct => ct.id !== t.id));
                      fetchAll();
                    } catch (e) { alert('Restore failed'); }
                  }} style={{ fontSize: 10, padding: '3px 10px', background: '#E1F5EE', color: '#0F6E56', border: '0.5px solid #5DCAA5', borderRadius: 4, cursor: 'pointer' }}>
                    Restore
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {showCancelled && cancelledTrips.length === 0 && (
          <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>No cancelled trips today</div>
        )}
      </div>

      {showCreateModal && (
        <CreateTripModal onClose={() => setShowCreateModal(false)} onSaved={() => { fetchAll(); setShowCreateModal(false); }} />
      )}
      {assigningTrip && (
        <AssignmentPanel transport={assigningTrip} onClose={() => setAssigningTrip(null)} onDispatched={fetchAll} existingTransports={transports} />
      )}
      {rejectingTrip && (
        <RejectModal trip={rejectingTrip} sibling={findActiveSibling(rejectingTrip, transports)} onClose={() => setRejectingTrip(null)} onConfirm={handleReject} />
      )}
      {editingTrip && (
        <EditTripModal trip={editingTrip} onClose={() => setEditingTrip(null)} onSaved={() => { fetchAll(); setEditingTrip(null); }} />
      )}

      {showPriorities && (
        <PriorityModal drivers={drivers} onClose={() => setShowPriorities(false)} />
      )}

      {/* Run Rosie Run Modal */}
      {showRunRosie && rosieProposals && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500, padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 650, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ padding: '16px 20px', borderBottom: '0.5px solid #e0e0db', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0, color: '#1D9E75' }}>Run Rosie Run</h2>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                    {rosieProposals.plannable} trips analyzed · {rosieProposals.locked} in progress (locked)
                  </div>
                </div>
                <button onClick={() => setShowRunRosie(false)} style={{ fontSize: 13, padding: '4px 12px' }}>Close</button>
              </div>
              {/* Summary badges */}
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                {rosieProposals.newAssignments > 0 && <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: '#E8F5E9', color: '#2E7D32', fontWeight: 600 }}>{rosieProposals.newAssignments} new</span>}
                {rosieProposals.reassignments > 0 && <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: '#FFF3E0', color: '#E65100', fontWeight: 600 }}>{rosieProposals.reassignments} reassigned</span>}
                {rosieProposals.kept > 0 && <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: '#f5f5f3', color: '#888', fontWeight: 600 }}>{rosieProposals.kept} unchanged</span>}
                {rosieProposals.unfulfillable > 0 && <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: '#FFEBEE', color: '#C62828', fontWeight: 600 }}>{rosieProposals.unfulfillable} issues</span>}
              </div>
            </div>

            <div style={{ padding: '12px 20px' }}>
              {/* Driver utilization */}
              {rosieProposals.driverSummary && rosieProposals.driverSummary.length > 0 && (
                <div style={{ marginBottom: 12, background: '#f9f9f8', borderRadius: 8, padding: '8px 12px' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#666', marginBottom: 4 }}>Driver utilization</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {rosieProposals.driverSummary.map(d => (
                      <span key={d.id} style={{
                        fontSize: 10, padding: '2px 6px', borderRadius: 8,
                        background: d.idle ? '#FFEBEE' : d.assignedTrips >= 3 ? '#E8F5E9' : '#E6F1FB',
                        color: d.idle ? '#C62828' : '#333',
                      }}>
                        {d.name.split(' ')[0]} ({d.assignedTrips} trips, ~{d.todayEstHours}h today, {d.wtdHours}h WTD)
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {rosieProposals.proposals.length === 0 && (
                <div style={{ textAlign: 'center', padding: 30, color: '#999', fontSize: 13 }}>No trips to plan!</div>
              )}

              {/* Show unfulfillable first */}
              {rosieProposals.proposals.filter(p => !p.canFulfill).map(p => {
                const time = p.scheduledTime ? format(toET(p.scheduledTime), 'h:mm a') : '--';
                return (
                  <div key={p.transportId} style={{ border: '1px solid #FFCDD2', borderRadius: 10, padding: '12px 16px', marginBottom: 8, background: '#FFF8F8' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{time}</span>
                        <span style={{ color: '#888', fontSize: 12, marginLeft: 8 }}>{p.transportNumber}</span>
                        {p.requiresWheelchair && <span style={{ marginLeft: 6, fontSize: 10, background: '#EEEDFE', color: '#3C3489', padding: '1px 6px', borderRadius: 10 }}>WC</span>}
                        {p.patientType && ['201','302','304'].includes(p.patientType) && <span style={{ marginLeft: 4, fontSize: 10, background: '#FAEEDA', color: '#854F0B', padding: '1px 6px', borderRadius: 10 }}>BH {p.patientType}</span>}
                      </div>
                      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 600, background: '#FFEBEE', color: '#C62828' }}>Needs attention</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>{(p.destination || '').substring(0, 50)}</div>
                    <div style={{ fontSize: 12, color: '#C62828' }}>{p.reason}</div>
                  </div>
                );
              })}

              {/* New assignments */}
              {rosieProposals.proposals.filter(p => p.canFulfill && p.changeType === 'new').length > 0 && (
                <div style={{ fontSize: 12, fontWeight: 600, color: '#2E7D32', marginTop: 8, marginBottom: 4 }}>New assignments</div>
              )}
              {rosieProposals.proposals.filter(p => p.canFulfill && p.changeType === 'new').map(p => {
                const time = p.scheduledTime ? format(toET(p.scheduledTime), 'h:mm a') : '--';
                return (
                  <div key={p.transportId} style={{ border: '1px solid #C8E6C9', borderRadius: 10, padding: '12px 16px', marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{time}</span>
                        <span style={{ color: '#888', fontSize: 12, marginLeft: 8 }}>{p.transportNumber}</span>
                        {p.requiresWheelchair && <span style={{ marginLeft: 6, fontSize: 10, background: '#EEEDFE', color: '#3C3489', padding: '1px 6px', borderRadius: 10 }}>WC</span>}
                        <span style={{ marginLeft: 6, fontSize: 10, background: '#E8F5E9', color: '#2E7D32', padding: '1px 6px', borderRadius: 10 }}>NEW</span>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>{(p.destination || '').substring(0, 50)}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: 12 }}>
                        <span style={{ color: '#0C447C', fontWeight: 500 }}>{p.vehicle?.name}</span>
                        <span style={{ color: '#888', margin: '0 6px' }}>·</span>
                        {p.drivers.map((d, j) => (
                          <span key={j} style={{ fontSize: 11, padding: '1px 6px', borderRadius: 10, background: j === 0 ? '#E1F5EE' : '#E6F1FB', color: j === 0 ? '#085041' : '#0C447C', fontWeight: 500, marginRight: 4 }}>{d.name}</span>
                        ))}
                      </div>
                      <button onClick={async () => {
                        try {
                          await api.post(`/transports/${p.transportId}/assign`, { assignments: p.drivers.map(d => ({ driverId: d.id, role: d.role, hourlyRate: 40 })), vehicleId: p.vehicle.id, forceAssign: true });
                          setRosieProposals(prev => ({ ...prev, proposals: prev.proposals.map(pp => pp.transportId === p.transportId ? { ...pp, approved: true } : pp) }));
                        } catch (err) { alert('Assign failed: ' + (err.response?.data?.error || err.message)); }
                      }} disabled={p.approved} style={{ fontSize: 11, padding: '4px 12px', borderRadius: 6, border: 'none', fontWeight: 600, cursor: 'pointer', background: p.approved ? '#C8E6C9' : '#1D9E75', color: 'white' }}>
                        {p.approved ? 'Done' : 'Approve'}
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Reassignments */}
              {rosieProposals.proposals.filter(p => p.canFulfill && p.changeType === 'reassign').length > 0 && (
                <div style={{ fontSize: 12, fontWeight: 600, color: '#E65100', marginTop: 12, marginBottom: 4 }}>Suggested reassignments</div>
              )}
              {rosieProposals.proposals.filter(p => p.canFulfill && p.changeType === 'reassign').map(p => {
                const time = p.scheduledTime ? format(toET(p.scheduledTime), 'h:mm a') : '--';
                return (
                  <div key={p.transportId} style={{ border: '1px solid #FFE0B2', borderRadius: 10, padding: '12px 16px', marginBottom: 8, background: '#FFFBF5' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{time}</span>
                        <span style={{ color: '#888', fontSize: 12, marginLeft: 8 }}>{p.transportNumber}</span>
                        {p.requiresWheelchair && <span style={{ marginLeft: 6, fontSize: 10, background: '#EEEDFE', color: '#3C3489', padding: '1px 6px', borderRadius: 10 }}>WC</span>}
                        <span style={{ marginLeft: 6, fontSize: 10, background: '#FFF3E0', color: '#E65100', padding: '1px 6px', borderRadius: 10 }}>SWAP</span>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>{(p.destination || '').substring(0, 50)}</div>
                    {/* Current → Proposed */}
                    <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>
                      Currently: {p.currentVehicle?.name || '?'} · {p.currentDrivers.map(d => d.name).join(', ') || 'unassigned'}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: 12 }}>
                        <span style={{ color: '#E65100', fontWeight: 500 }}>→ {p.vehicle?.name}</span>
                        <span style={{ color: '#888', margin: '0 6px' }}>·</span>
                        {p.drivers.map((d, j) => (
                          <span key={j} style={{ fontSize: 11, padding: '1px 6px', borderRadius: 10, background: '#FFF3E0', color: '#E65100', fontWeight: 500, marginRight: 4 }}>{d.name}</span>
                        ))}
                      </div>
                      <button onClick={async () => {
                        try {
                          await api.post(`/transports/${p.transportId}/assign`, { assignments: p.drivers.map(d => ({ driverId: d.id, role: d.role, hourlyRate: 40 })), vehicleId: p.vehicle.id, forceAssign: true });
                          setRosieProposals(prev => ({ ...prev, proposals: prev.proposals.map(pp => pp.transportId === p.transportId ? { ...pp, approved: true } : pp) }));
                        } catch (err) { alert('Assign failed: ' + (err.response?.data?.error || err.message)); }
                      }} disabled={p.approved} style={{ fontSize: 11, padding: '4px 12px', borderRadius: 6, border: 'none', fontWeight: 600, cursor: 'pointer', background: p.approved ? '#C8E6C9' : '#E65100', color: 'white' }}>
                        {p.approved ? 'Done' : 'Swap'}
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Kept (collapsed) */}
              {rosieProposals.proposals.filter(p => p.changeType === 'keep').length > 0 && (
                <div style={{ fontSize: 12, color: '#888', marginTop: 12, marginBottom: 4 }}>
                  {rosieProposals.kept} trips look good as-is
                </div>
              )}

              {/* Approve all button — only for new + reassignments */}
              {rosieProposals.proposals.some(p => p.canFulfill && p.changeType !== 'keep' && !p.approved) && (
                <button onClick={async () => {
                  const toApprove = rosieProposals.proposals.filter(p => p.canFulfill && p.changeType !== 'keep' && !p.approved);
                  for (const p of toApprove) {
                    try {
                      await api.post(`/transports/${p.transportId}/assign`, { assignments: p.drivers.map(d => ({ driverId: d.id, role: d.role, hourlyRate: 40 })), vehicleId: p.vehicle.id, forceAssign: true });
                      setRosieProposals(prev => ({ ...prev, proposals: prev.proposals.map(pp => pp.transportId === p.transportId ? { ...pp, approved: true } : pp) }));
                    } catch (err) {
                      if (err.response?.data?.requiresSplitConfirm) alert('Skipped: ' + err.response.data.message);
                    }
                  }
                  fetchAll();
                }} style={{ width: '100%', fontSize: 14, padding: '12px', marginTop: 12, background: '#1D9E75', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
                  Approve all changes ({rosieProposals.proposals.filter(p => p.canFulfill && p.changeType !== 'keep' && !p.approved).length})
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {mergingTrip && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: '1.5rem', width: '100%', maxWidth: 450, margin: '0 16px', maxHeight: '80vh', overflow: 'auto' }}>
            <h2 style={{ fontSize: 16, fontWeight: 500, marginBottom: 4 }}>Merge trips</h2>
            <p style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
              Keep <strong>{mergingTrip.transport_number}</strong> and merge another trip into it. The duplicate will be cancelled and its fax/data will be moved over.
            </p>
            <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>Select the trip to merge with (its fax and data will be combined, the other cancelled):</div>
            {mergeTargets
              .filter(t => t.id !== mergingTrip.id)
              .sort((a, b) => {
                // Show non-cancelled first
                if (a.status === 'cancelled' && b.status !== 'cancelled') return 1;
                if (a.status !== 'cancelled' && b.status === 'cancelled') return -1;
                return new Date(a.scheduled_time) - new Date(b.scheduled_time);
              })
              .map(t => {
                const time = t.scheduled_time ? format(toET(t.scheduled_time), 'EEE h:mm a') : '?';
                const isCancelled = t.status === 'cancelled';
                // Determine which to keep: prefer the dispatched/assigned one
                const keepStatuses = ['dispatched', 'en_route_pickup', 'picked_up', 'completed'];
                const keepId = keepStatuses.includes(t.status) ? t.id : mergingTrip.id;
                const dupeId = keepId === t.id ? mergingTrip.id : t.id;
                const keepNum = keepId === t.id ? t.transport_number : mergingTrip.transport_number;
                const dupeNum = keepId === t.id ? mergingTrip.transport_number : t.transport_number;
                return (
                  <div key={t.id} onClick={() => {
                    if (window.confirm(`Keep ${keepNum} and merge data from ${dupeNum}. ${dupeNum} will be cancelled. Continue?`)) {
                      handleMerge(keepId, dupeId);
                    }
                  }} style={{
                    opacity: isCancelled ? 0.5 : 1,
                    padding: '8px 12px', border: '0.5px solid #e0e0db', borderRadius: 8, marginBottom: 6, cursor: 'pointer',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 500 }}>{t.transport_number} — {time}</div>
                      <div style={{ fontSize: 11, color: '#666' }}>{(t.destination_address || '').substring(0, 45)}</div>
                      <div style={{ fontSize: 10, color: '#999' }}>{t.request_channel} · {t.status}</div>
                    </div>
                    {t.fax_file_id && <span style={{ fontSize: 9, background: '#E6F1FB', color: '#185FA5', padding: '2px 6px', borderRadius: 10 }}>has fax</span>}
                  </div>
                );
              })}
            <button onClick={() => setMergingTrip(null)} style={{ width: '100%', fontSize: 13, padding: '8px', marginTop: 8 }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}